// src/components/PdfViewer.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as pdfjsLib from "pdfjs-dist/webpack.mjs";

/**
 * PdfViewer
 *
 * A complete AskLawly PDF viewing component. It owns the full PDF experience:
 * loading, rendering, page navigation, thumbnails, outline, search, zoom,
 * fit-width, fit-page, rotation, links, text selection, fullscreen, printing,
 * downloading, password prompts, document properties, loading/error states,
 * mobile responsiveness, and browser fallback.
 *
 * It does not depend on another viewer shell or external toolbar component.
 *
 * Required package:
 *   npm install pdfjs-dist
 *
 * Recommended current package target:
 *   pdfjs-dist >= 6.1.200 < 7
 *
 * Props:
 *  - src: string | Blob | File | ArrayBuffer | Uint8Array | {
 *      url?: string,
 *      data?: ArrayBuffer | Uint8Array
 *    }
 *  - title?: string
 *  - filename?: string
 *  - downloadUrl?: string
 *  - fallbackUrl?: string
 *  - getBrowserFallbackUrl?: ({
 *      src,
 *      title,
 *      filename,
 *      pdfDocument
 *    }) => Promise<string | { url?: string }> | string | { url?: string }
 *    Request a browser-openable URL at click time. File Access Gateway callers
 *    use this to obtain a short-lived opaque AskLawly browser ticket; legacy
 *    callers may still return a fresh public/signed inline URL while they are
 *    migrated.
 *  - httpHeaders?: Record<string, string>
 *    Headers used by PDF.js and authenticated fetches. When an Authorization
 *    header is present, PdfViewer will never navigate the browser directly to
 *    src/downloadUrl because top-level navigation cannot carry that header.
 *  - withCredentials?: boolean
 *  - initialPage?: number
 *  - initialZoom?: "fit-width" | "fit-page" | number
 *  - initialRotation?: 0 | 90 | 180 | 270
 *  - defaultSidebarOpen?: boolean
 *  - allowDownload?: boolean
 *  - allowPrint?: boolean
 *  - allowFullscreen?: boolean
 *  - allowBrowserFallback?: boolean
 *  - showTitle?: boolean
 *  - className?: string
 *  - style?: React.CSSProperties
 *  - cMapUrl?: string
 *  - standardFontDataUrl?: string
 *  - wasmUrl?: string
 *  - onLoad?: ({ pdfDocument, pages, metadata }) => void
 *  - onError?: (error: Error) => void
 *  - onPageChange?: (pageNumber: number) => void
 *  - onDownload?: ({ blob, filename }) => Promise<void> | void
 *  - onPrint?: ({ blob, filename }) => Promise<void> | void
 */

const BRAND = "#1E2A78";
const TOOLBAR_BG = "#343434";
const TOOLBAR_BORDER = "#505050";
const VIEWER_BG = "#242424";
const SIDEBAR_BG = "#2B2B2B";
const SIDEBAR_BORDER = "#494949";
const TEXT_LIGHT = "#F8FAFC";
const TEXT_MUTED = "#CBD5E1";

const MIN_ZOOM = 25;
const MAX_ZOOM = 500;
const ZOOM_STEP = 10;
const THUMBNAIL_WIDTH = 150;
const NEAR_PAGE_MARGIN = "900px 0px";
const SEARCH_BATCH_YIELD = 5;
const PRINT_CLEANUP_MS = 60_000;
const OBJECT_URL_CLEANUP_MS = 10 * 60_000;

const ZOOM_OPTIONS = [
  { value: "fit-width", label: "Fit width" },
  { value: "fit-page", label: "Fit page" },
  { value: "custom:50", label: "50%" },
  { value: "custom:75", label: "75%" },
  { value: "custom:100", label: "100%" },
  { value: "custom:125", label: "125%" },
  { value: "custom:150", label: "150%" },
  { value: "custom:200", label: "200%" },
];

const EMPTY_METADATA = {
  title: "",
  author: "",
  subject: "",
  keywords: "",
  creator: "",
  producer: "",
  creationDate: "",
  modificationDate: "",
  pdfVersion: "",
  linearized: "",
  tagged: "",
  fileSize: "",
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function safeString(value) {
  return String(value ?? "").trim();
}

function ensurePdfFilename(value) {
  const clean = safeString(value || "document")
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 180);

  if (!clean) return "document.pdf";
  return /\.pdf$/i.test(clean) ? clean : `${clean}.pdf`;
}

function formatBytes(bytes) {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return "";

  const units = ["B", "KB", "MB", "GB"];
  let size = value;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  const decimals = unitIndex === 0 || size >= 100 ? 0 : size >= 10 ? 1 : 2;
  return `${size.toFixed(decimals)} ${units[unitIndex]}`;
}

function parsePdfDate(value) {
  const raw = safeString(value);
  if (!raw) return "";

  const normalized = raw.replace(/^D:/, "");
  const match = normalized.match(
    /^(\d{4})(\d{2})?(\d{2})?(\d{2})?(\d{2})?(\d{2})?/
  );

  if (!match) return raw;

  const [, year, month = "01", day = "01", hour = "00", minute = "00", second = "00"] =
    match;

  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second)
  );

  return Number.isNaN(date.getTime()) ? raw : date.toLocaleString();
}

function escapeRegExp(value) {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildSearchRegex(term, caseSensitive, wholeWord) {
  const source = escapeRegExp(term);
  const wrapped = wholeWord ? `\\b${source}\\b` : source;
  return new RegExp(wrapped, caseSensitive ? "g" : "gi");
}

function countMatches(text, regex) {
  if (!text || !regex) return 0;

  const clone = new RegExp(regex.source, regex.flags);
  let count = 0;
  let match;

  while ((match = clone.exec(text))) {
    count += 1;
    if (match[0] === "") clone.lastIndex += 1;
  }

  return count;
}

function textMatches(value, term, caseSensitive, wholeWord) {
  if (!term) return false;
  return countMatches(String(value || ""), buildSearchRegex(term, caseSensitive, wholeWord)) > 0;
}

function sourceToKey(src) {
  if (typeof src === "string") return `url:${src}`;
  if (typeof Blob !== "undefined" && src instanceof Blob) {
    return `blob:${src.size}:${src.type}:${src.lastModified || ""}`;
  }
  if (src instanceof ArrayBuffer) return `array:${src.byteLength}`;
  if (ArrayBuffer.isView(src)) return `view:${src.byteLength}`;
  if (src && typeof src === "object") {
    if (typeof src.url === "string") return `object-url:${src.url}`;
    if (src.data instanceof ArrayBuffer) return `object-array:${src.data.byteLength}`;
    if (ArrayBuffer.isView(src.data)) return `object-view:${src.data.byteLength}`;
  }
  return `source:${String(src || "")}`;
}

function normalizeRotation(value) {
  const normalized = ((Number(value || 0) % 360) + 360) % 360;
  return [0, 90, 180, 270].includes(normalized) ? normalized : 0;
}

function isEditableTarget(target) {
  const tag = target?.tagName?.toLowerCase?.();
  return (
    tag === "input" ||
    tag === "textarea" ||
    tag === "select" ||
    target?.isContentEditable
  );
}

function getSafeExternalUrl(value) {
  const raw = safeString(value);
  if (!raw) return "";

  try {
    const url = new URL(raw, window.location.href);
    if (["http:", "https:", "mailto:", "tel:"].includes(url.protocol)) {
      return url.toString();
    }
  } catch {
    return "";
  }

  return "";
}

function triggerBlobDownload(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = objectUrl;
  anchor.download = ensurePdfFilename(filename);
  anchor.rel = "noopener";
  anchor.style.display = "none";

  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  window.setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, 1500);
}

function makeBlobFromTypedData(data) {
  if (data instanceof ArrayBuffer) {
    return new Blob([data], { type: "application/pdf" });
  }

  if (ArrayBuffer.isView(data)) {
    return new Blob(
      [
        data.buffer.slice(
          data.byteOffset,
          data.byteOffset + data.byteLength
        ),
      ],
      { type: "application/pdf" }
    );
  }

  return null;
}

function normalizeDocumentSource(src) {
  if (typeof src === "string") {
    return { url: src };
  }

  if (typeof Blob !== "undefined" && src instanceof Blob) {
    return { blob: src };
  }

  const typedBlob = makeBlobFromTypedData(src);
  if (typedBlob) {
    return { data: src };
  }

  if (src && typeof src === "object") {
    if (typeof src.url === "string") {
      return { url: src.url };
    }

    const nestedBlob = makeBlobFromTypedData(src.data);
    if (nestedBlob) {
      return { data: src.data };
    }
  }

  return {};
}

function fallbackResultUrl(value) {
  if (typeof value === "string") {
    return safeString(value);
  }

  if (value && typeof value === "object") {
    return safeString(value.url);
  }

  return "";
}

function hasAuthorizationHeader(headers) {
  if (!headers || typeof headers !== "object") return false;

  return Object.keys(headers).some(
    (key) => safeString(key).toLowerCase() === "authorization"
  );
}

function isBrowserOpenablePdfUrl(value) {
  const raw = safeString(value);
  if (!raw) return false;

  if (raw.startsWith("blob:")) return true;

  try {
    const url = new URL(raw, window.location.href);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function prepareBrowserFallbackWindow(targetWindow, title) {
  if (!targetWindow) return;

  try {
    targetWindow.opener = null;
    targetWindow.document.title = "Opening PDF…";
    targetWindow.document.documentElement.style.background = "#F8FAFC";
    targetWindow.document.body.replaceChildren();
    targetWindow.document.body.style.margin = "0";
    targetWindow.document.body.style.minHeight = "100vh";
    targetWindow.document.body.style.display = "grid";
    targetWindow.document.body.style.placeItems = "center";
    targetWindow.document.body.style.padding = "24px";
    targetWindow.document.body.style.boxSizing = "border-box";
    targetWindow.document.body.style.fontFamily =
      "Arial, Helvetica, sans-serif";
    targetWindow.document.body.style.color = "#0F172A";

    const card = targetWindow.document.createElement("div");
    card.style.width = "min(420px, 100%)";
    card.style.padding = "24px";
    card.style.border = "1px solid #D8E0EA";
    card.style.borderRadius = "16px";
    card.style.background = "#FFFFFF";
    card.style.boxShadow = "0 14px 34px rgba(15,23,42,.10)";
    card.style.textAlign = "center";
    card.style.boxSizing = "border-box";

    const spinner = targetWindow.document.createElement("div");
    spinner.style.width = "34px";
    spinner.style.height = "34px";
    spinner.style.margin = "0 auto 14px";
    spinner.style.borderRadius = "999px";
    spinner.style.border = "4px solid rgba(30,42,120,.16)";
    spinner.style.borderTopColor = "#1E2A78";

    const heading = targetWindow.document.createElement("div");
    heading.textContent = "Opening PDF";
    heading.style.fontSize = "17px";
    heading.style.fontWeight = "800";

    const message = targetWindow.document.createElement("div");
    message.textContent = safeString(title)
      ? `Preparing ${safeString(title)} for the browser viewer.`
      : "Preparing the document for the browser viewer.";
    message.style.marginTop = "7px";
    message.style.color = "#64748B";
    message.style.fontSize = "13px";
    message.style.lineHeight = "1.5";

    const style = targetWindow.document.createElement("style");
    style.textContent = `
      @keyframes asklawlyPdfFallbackSpin {
        to { transform: rotate(360deg); }
      }
    `;
    spinner.style.animation =
      "asklawlyPdfFallbackSpin .8s linear infinite";

    card.append(spinner, heading, message);
    targetWindow.document.head.appendChild(style);
    targetWindow.document.body.appendChild(card);
  } catch {
    // Some browsers restrict modification of the temporary blank tab.
  }
}

function createDocumentParams({
  src,
  httpHeaders,
  withCredentials,
  cMapUrl,
  standardFontDataUrl,
  wasmUrl,
}) {
  const normalized = normalizeDocumentSource(src);
  const params = {
    httpHeaders: httpHeaders || undefined,
    withCredentials: Boolean(withCredentials),
    useSystemFonts: true,
    isEvalSupported: false,
    enableXfa: true,
    stopAtErrors: false,
  };

  if (cMapUrl) {
    params.cMapUrl = cMapUrl;
    params.cMapPacked = true;
  }

  if (standardFontDataUrl) {
    params.standardFontDataUrl = standardFontDataUrl;
  }

  if (wasmUrl) {
    params.wasmUrl = wasmUrl;
  }

  if (normalized.url) {
    params.url = normalized.url;
  } else if (normalized.data) {
    params.data =
      normalized.data instanceof Uint8Array
        ? normalized.data
        : new Uint8Array(
            normalized.data.buffer || normalized.data,
            normalized.data.byteOffset || 0,
            normalized.data.byteLength || normalized.data.byteLength
          );
  }

  return params;
}

function resolveScale({
  mode,
  customPercent,
  pageWidth,
  pageHeight,
  availableWidth,
  availableHeight,
}) {
  if (!pageWidth || !pageHeight) {
    return Math.max(0.1, Number(customPercent || 100) / 100);
  }

  if (mode === "fit-width") {
    return clamp(
      Math.max(160, availableWidth - 36) / pageWidth,
      MIN_ZOOM / 100,
      MAX_ZOOM / 100
    );
  }

  if (mode === "fit-page") {
    const widthScale = Math.max(160, availableWidth - 36) / pageWidth;
    const heightScale = Math.max(240, availableHeight - 36) / pageHeight;

    return clamp(
      Math.min(widthScale, heightScale),
      MIN_ZOOM / 100,
      MAX_ZOOM / 100
    );
  }

  return clamp(
    Number(customPercent || 100) / 100,
    MIN_ZOOM / 100,
    MAX_ZOOM / 100
  );
}

function viewportRectangle(viewport, rectangle) {
  // PDF.js 6.1.200+ removed the legacy PageViewport rectangle-conversion helper.
  // Convert the PDF annotation rectangle with the viewport affine transform
  // directly so link annotations remain compatible with the supported PDF.js 6.x
  // range and cannot crash React when a later page is lazy-rendered while scrolling.
  const rect = Array.isArray(rectangle)
    ? rectangle.slice(0, 4).map((value) => Number(value))
    : [];

  const transform = Array.from(viewport?.transform || []).map((value) =>
    Number(value)
  );

  if (
    rect.length !== 4 ||
    rect.some((value) => !Number.isFinite(value)) ||
    transform.length < 6 ||
    transform.slice(0, 6).some((value) => !Number.isFinite(value))
  ) {
    return { left: 0, top: 0, width: 0, height: 0 };
  }

  const [a, b, c, d, e, f] = transform;
  const [x1, y1, x2, y2] = rect;

  const applyTransform = (x, y) => ({
    x: a * x + c * y + e,
    y: b * x + d * y + f,
  });

  // Transform all four corners rather than only two opposite corners. This keeps
  // the annotation box correct for 0, 90, 180, and 270 degree page rotations.
  const points = [
    applyTransform(x1, y1),
    applyTransform(x1, y2),
    applyTransform(x2, y1),
    applyTransform(x2, y2),
  ];

  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);

  const left = Math.min(...xs);
  const right = Math.max(...xs);
  const top = Math.min(...ys);
  const bottom = Math.max(...ys);

  return {
    left,
    top,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
}

function Icon({ name, size = 20, strokeWidth = 1.9 }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  const paths = {
    menu: (
      <>
        <path d="M4 6h16" />
        <path d="M4 12h16" />
        <path d="M4 18h16" />
      </>
    ),
    thumbnails: (
      <>
        <rect x="3" y="3" width="7" height="8" rx="1" />
        <rect x="14" y="3" width="7" height="8" rx="1" />
        <rect x="3" y="15" width="7" height="6" rx="1" />
        <rect x="14" y="15" width="7" height="6" rx="1" />
      </>
    ),
    outline: (
      <>
        <path d="M9 6h11" />
        <path d="M9 12h11" />
        <path d="M9 18h11" />
        <path d="M4 6h.01" />
        <path d="M4 12h.01" />
        <path d="M4 18h.01" />
      </>
    ),
    attachment: (
      <path d="m21.44 11.05-8.49 8.49a5 5 0 0 1-7.07-7.07l9.19-9.19a3.5 3.5 0 0 1 4.95 4.95l-9.2 9.19a2 2 0 1 1-2.83-2.83l8.49-8.48" />
    ),
    minus: <path d="M5 12h14" />,
    plus: (
      <>
        <path d="M12 5v14" />
        <path d="M5 12h14" />
      </>
    ),
    fitWidth: (
      <>
        <rect x="5" y="3" width="14" height="18" rx="1" />
        <path d="M8 12h8" />
        <path d="m10 10-2 2 2 2" />
        <path d="m14 10 2 2-2 2" />
      </>
    ),
    fitPage: (
      <>
        <rect x="6" y="3" width="12" height="18" rx="1" />
        <path d="m9 8 3-3 3 3" />
        <path d="m9 16 3 3 3-3" />
      </>
    ),
    rotate: (
      <>
        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
        <path d="M3 3v5h5" />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-4-4" />
      </>
    ),
    chevronUp: <path d="m18 15-6-6-6 6" />,
    chevronDown: <path d="m6 9 6 6 6-6" />,
    chevronLeft: <path d="m15 18-6-6 6-6" />,
    chevronRight: <path d="m9 18 6-6-6-6" />,
    firstPage: (
      <>
        <path d="M6 5v14" />
        <path d="m17 6-6 6 6 6" />
      </>
    ),
    lastPage: (
      <>
        <path d="M18 5v14" />
        <path d="m7 6 6 6-6 6" />
      </>
    ),
    download: (
      <>
        <path d="M12 3v12" />
        <path d="m7 10 5 5 5-5" />
        <path d="M5 21h14" />
      </>
    ),
    print: (
      <>
        <path d="M6 9V3h12v6" />
        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
        <rect x="6" y="14" width="12" height="7" />
      </>
    ),
    fullscreen: (
      <>
        <path d="M8 3H3v5" />
        <path d="M16 3h5v5" />
        <path d="M8 21H3v-5" />
        <path d="M16 21h5v-5" />
      </>
    ),
    fullscreenExit: (
      <>
        <path d="M9 4v5H4" />
        <path d="M15 4v5h5" />
        <path d="M9 20v-5H4" />
        <path d="M15 20v-5h5" />
      </>
    ),
    more: (
      <>
        <circle cx="12" cy="5" r="1" fill="currentColor" stroke="none" />
        <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
        <circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
      </>
    ),
    close: (
      <>
        <path d="M6 6l12 12" />
        <path d="M18 6 6 18" />
      </>
    ),
    retry: (
      <>
        <path d="M20 11a8 8 0 1 0-2.3 5.7" />
        <path d="M20 4v7h-7" />
      </>
    ),
    browser: (
      <>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M3 9h18" />
        <path d="m14 13 4-4" />
        <path d="M14 9h4v4" />
      </>
    ),
    info: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5" />
        <path d="M12 8h.01" />
      </>
    ),
    lock: (
      <>
        <rect x="5" y="10" width="14" height="11" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </>
    ),
  };

  return <svg {...common}>{paths[name] || paths.info}</svg>;
}

function ToolbarButton({
  icon,
  label,
  title,
  active = false,
  disabled = false,
  onClick,
  compact = false,
  className = "",
}) {
  return (
    <button
      type="button"
      className={`pdf-viewer__toolbar-button ${
        active ? "is-active" : ""
      } ${compact ? "is-compact" : ""} ${className}`}
      onClick={onClick}
      disabled={disabled}
      title={title || label}
      aria-label={label}
      aria-pressed={active || undefined}
    >
      {icon ? <Icon name={icon} size={19} /> : null}
      {!compact && label ? (
        <span className="pdf-viewer__toolbar-label">{label}</span>
      ) : null}
    </button>
  );
}

function Modal({
  open,
  title,
  children,
  onClose,
  width = 520,
}) {
  if (!open) return null;

  return (
    <div
      className="pdf-viewer__dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className="pdf-viewer__dialog"
        style={{ width: `min(${width}px, calc(100vw - 28px))` }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="pdf-viewer__dialog-header">
          <strong>{title}</strong>
          <button
            type="button"
            className="pdf-viewer__dialog-close"
            onClick={onClose}
            aria-label="Close"
            title="Close"
          >
            <Icon name="close" size={18} />
          </button>
        </div>
        <div className="pdf-viewer__dialog-body">{children}</div>
      </div>
    </div>
  );
}

function PasswordDialog({
  open,
  incorrect,
  busy,
  onSubmit,
  onCancel,
}) {
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (open) setPassword("");
  }, [open, incorrect]);

  if (!open) return null;

  return (
    <div className="pdf-viewer__dialog-backdrop" role="presentation">
      <form
        className="pdf-viewer__dialog pdf-viewer__password-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Password required"
        onSubmit={(event) => {
          event.preventDefault();
          if (password) onSubmit?.(password);
        }}
      >
        <div className="pdf-viewer__password-icon">
          <Icon name="lock" size={22} />
        </div>

        <h3>Password required</h3>
        <p>
          {incorrect
            ? "That password was not accepted. Enter the document password again."
            : "This PDF is protected. Enter its password to continue."}
        </p>

        <input
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoFocus
          autoComplete="current-password"
          placeholder="Document password"
          aria-label="Document password"
          disabled={busy}
        />

        <div className="pdf-viewer__password-actions">
          <button
            type="button"
            className="pdf-viewer__secondary-action"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="pdf-viewer__primary-action"
            disabled={!password || busy}
          >
            {busy ? "Opening…" : "Open PDF"}
          </button>
        </div>
      </form>
    </div>
  );
}

function OutlineTree({
  items,
  depth = 0,
  onNavigate,
}) {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  return (
    <div className="pdf-viewer__outline-tree">
      {items.map((item, index) => (
        <div
          key={`${depth}-${index}-${item.title || "outline"}`}
          className="pdf-viewer__outline-node"
        >
          <button
            type="button"
            className="pdf-viewer__outline-link"
            style={{ paddingLeft: `${10 + depth * 14}px` }}
            onClick={() => onNavigate?.(item.dest)}
            title={item.title || "Untitled section"}
          >
            {item.title || "Untitled section"}
          </button>

          {Array.isArray(item.items) && item.items.length ? (
            <OutlineTree
              items={item.items}
              depth={depth + 1}
              onNavigate={onNavigate}
            />
          ) : null}
        </div>
      ))}
    </div>
  );
}

function PdfThumbnail({
  pdfDocument,
  pageNumber,
  active,
  rotation,
  onClick,
}) {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const [visible, setVisible] = useState(pageNumber <= 4);
  const [ratio, setRatio] = useState(11 / 8.5);

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node || visible || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "500px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    let cancelled = false;
    let renderTask = null;

    if (!visible || !pdfDocument) return undefined;

    (async () => {
      const page = await pdfDocument.getPage(pageNumber);
      if (cancelled) return;

      const baseViewport = page.getViewport({
        scale: 1,
        rotation,
      });

      setRatio(baseViewport.height / baseViewport.width);

      const scale = THUMBNAIL_WIDTH / baseViewport.width;
      const viewport = page.getViewport({
        scale,
        rotation,
      });

      const canvas = canvasRef.current;
      if (!canvas) return;

      const outputScale = Math.min(window.devicePixelRatio || 1, 1.5);
      const context = canvas.getContext("2d", { alpha: false });

      canvas.width = Math.max(1, Math.floor(viewport.width * outputScale));
      canvas.height = Math.max(1, Math.floor(viewport.height * outputScale));
      canvas.style.width = `${Math.round(viewport.width)}px`;
      canvas.style.height = `${Math.round(viewport.height)}px`;

      renderTask = page.render({
        canvasContext: context,
        viewport,
        transform:
          outputScale === 1
            ? null
            : [outputScale, 0, 0, outputScale, 0, 0],
        background: "#FFFFFF",
      });

      await renderTask.promise;
    })().catch((error) => {
      if (error?.name !== "RenderingCancelledException") {
        console.error("[PdfViewer] thumbnail render failed:", error);
      }
    });

    return () => {
      cancelled = true;
      try {
        renderTask?.cancel?.();
      } catch {
        // Best-effort thumbnail cancellation.
      }
    };
  }, [pdfDocument, pageNumber, rotation, visible]);

  return (
    <button
      ref={wrapperRef}
      type="button"
      className={`pdf-viewer__thumbnail ${active ? "is-active" : ""}`}
      onClick={() => onClick?.(pageNumber)}
      aria-label={`Go to page ${pageNumber}`}
      title={`Page ${pageNumber}`}
    >
      <div
        className="pdf-viewer__thumbnail-paper"
        style={{
          width: THUMBNAIL_WIDTH,
          height: Math.round(THUMBNAIL_WIDTH * ratio),
        }}
      >
        {visible ? <canvas ref={canvasRef} /> : null}
      </div>
      <span>{pageNumber}</span>
    </button>
  );
}

function PdfPage({
  pdfDocument,
  pageNumber,
  zoomMode,
  zoomPercent,
  rotation,
  availableWidth,
  availableHeight,
  searchTerm,
  searchCaseSensitive,
  searchWholeWord,
  activeSearchPage,
  onRegister,
  onNavigateDestination,
}) {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const [page, setPage] = useState(null);
  const [baseSize, setBaseSize] = useState({
    width: 612,
    height: 792,
  });
  const [nearViewport, setNearViewport] = useState(pageNumber <= 2);
  const [renderState, setRenderState] = useState("idle");
  const [textItems, setTextItems] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    onRegister?.(pageNumber, wrapperRef.current);

    return () => {
      onRegister?.(pageNumber, null);
    };
  }, [onRegister, pageNumber]);

  useEffect(() => {
    const node = wrapperRef.current;

    if (
      !node ||
      nearViewport ||
      typeof IntersectionObserver === "undefined"
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNearViewport(true);
        }
      },
      {
        root: node.closest(".pdf-viewer__document-scroll"),
        rootMargin: NEAR_PAGE_MARGIN,
        threshold: 0,
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [nearViewport]);

  useEffect(() => {
    let cancelled = false;

    setPage(null);
    setError("");
    setRenderState("idle");
    setTextItems([]);
    setAnnotations([]);

    if (!pdfDocument) return undefined;

    (async () => {
      const loadedPage = await pdfDocument.getPage(pageNumber);
      if (cancelled) return;

      const viewport = loadedPage.getViewport({
        scale: 1,
        rotation,
      });

      setPage(loadedPage);
      setBaseSize({
        width: viewport.width,
        height: viewport.height,
      });
    })().catch((loadError) => {
      if (!cancelled) {
        console.error("[PdfViewer] page load failed:", loadError);
        setError("This page could not be loaded.");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [pdfDocument, pageNumber, rotation]);

  const scale = useMemo(
    () =>
      resolveScale({
        mode: zoomMode,
        customPercent: zoomPercent,
        pageWidth: baseSize.width,
        pageHeight: baseSize.height,
        availableWidth,
        availableHeight,
      }),
    [
      zoomMode,
      zoomPercent,
      baseSize.width,
      baseSize.height,
      availableWidth,
      availableHeight,
    ]
  );

  const viewport = useMemo(() => {
    if (!page) {
      return {
        width: baseSize.width * scale,
        height: baseSize.height * scale,
        scale,
      };
    }

    return page.getViewport({
      scale,
      rotation,
    });
  }, [page, baseSize.width, baseSize.height, scale, rotation]);

  useEffect(() => {
    let cancelled = false;
    let renderTask = null;

    if (!page || !nearViewport) return undefined;

    (async () => {
      setRenderState("rendering");
      setError("");

      const canvas = canvasRef.current;
      if (!canvas) return;

      const context = canvas.getContext("2d", { alpha: false });
      const outputScale = Math.min(window.devicePixelRatio || 1, 2);

      canvas.width = Math.max(
        1,
        Math.floor(viewport.width * outputScale)
      );
      canvas.height = Math.max(
        1,
        Math.floor(viewport.height * outputScale)
      );
      canvas.style.width = `${Math.round(viewport.width)}px`;
      canvas.style.height = `${Math.round(viewport.height)}px`;

      try {
        renderTaskRef.current?.cancel?.();
      } catch {
        // Best-effort replacement of a previous render.
      }

      renderTask = page.render({
        canvasContext: context,
        viewport,
        transform:
          outputScale === 1
            ? null
            : [outputScale, 0, 0, outputScale, 0, 0],
        background: "#FFFFFF",
        intent: "display",
      });

      renderTaskRef.current = renderTask;
      await renderTask.promise;

      const [textContent, annotationItems] = await Promise.all([
        page.getTextContent({
          includeMarkedContent: true,
          disableNormalization: false,
        }),
        page.getAnnotations({ intent: "display" }),
      ]);

      if (cancelled) return;

      setTextItems(
        Array.isArray(textContent?.items)
          ? textContent.items.filter((item) => item?.str)
          : []
      );
      setAnnotations(
        Array.isArray(annotationItems) ? annotationItems : []
      );
      setRenderState("ready");
    })().catch((renderError) => {
      if (
        !cancelled &&
        renderError?.name !== "RenderingCancelledException"
      ) {
        console.error("[PdfViewer] page render failed:", renderError);
        setError("This page could not be rendered.");
        setRenderState("error");
      }
    });

    return () => {
      cancelled = true;
      try {
        renderTask?.cancel?.();
      } catch {
        // Best-effort page cancellation.
      }
    };
  }, [page, nearViewport, viewport]);

  const textSpans = useMemo(() => {
    if (!page || !textItems.length) return [];

    return textItems.map((item, index) => {
      const transform = pdfjsLib.Util.transform(
        viewport.transform,
        item.transform
      );

      const angle = Math.atan2(transform[1], transform[0]);
      const fontHeight = Math.max(
        1,
        Math.hypot(transform[2], transform[3])
      );
      const width = Math.max(
        1,
        Math.abs(Number(item.width || 0) * viewport.scale)
      );

      const matched = textMatches(
        item.str,
        searchTerm,
        searchCaseSensitive,
        searchWholeWord
      );

      return {
        key: `${pageNumber}-${index}`,
        text: item.str,
        matched,
        style: {
          left: `${transform[4]}px`,
          top: `${transform[5] - fontHeight}px`,
          width: `${width}px`,
          height: `${fontHeight * 1.15}px`,
          fontSize: `${fontHeight}px`,
          transform: `rotate(${angle}rad)`,
          transformOrigin: "0 0",
          direction: item.dir || "ltr",
        },
      };
    });
  }, [
    page,
    pageNumber,
    textItems,
    viewport,
    searchTerm,
    searchCaseSensitive,
    searchWholeWord,
  ]);

  const annotationLinks = useMemo(() => {
    if (!page || !annotations.length) return [];

    return annotations
      .filter(
        (annotation) =>
          annotation?.subtype === "Link" &&
          Array.isArray(annotation.rect)
      )
      .map((annotation, index) => {
        const box = viewportRectangle(viewport, annotation.rect);
        const externalUrl = getSafeExternalUrl(
          annotation.url || annotation.unsafeUrl
        );

        return {
          key: annotation.id || `${pageNumber}-annotation-${index}`,
          box,
          externalUrl,
          destination: annotation.dest || "",
          title:
            annotation.title ||
            annotation.contents ||
            externalUrl ||
            "Open link",
        };
      });
  }, [page, annotations, viewport, pageNumber]);

  return (
    <section
      ref={wrapperRef}
      className={`pdf-viewer__page-shell ${
        activeSearchPage ? "has-active-search" : ""
      }`}
      data-pdf-page={pageNumber}
      aria-label={`Page ${pageNumber}`}
      style={{
        width: `${Math.ceil(viewport.width)}px`,
        minHeight: `${Math.ceil(viewport.height)}px`,
      }}
    >
      <div
        className="pdf-viewer__page-paper"
        style={{
          width: `${Math.ceil(viewport.width)}px`,
          height: `${Math.ceil(viewport.height)}px`,
        }}
      >
        {nearViewport ? (
          <canvas
            ref={canvasRef}
            className="pdf-viewer__page-canvas"
          />
        ) : null}

        {renderState !== "ready" && !error ? (
          <div className="pdf-viewer__page-loading">
            <span className="pdf-viewer__spinner" />
            <span>Loading page {pageNumber}…</span>
          </div>
        ) : null}

        {error ? (
          <div className="pdf-viewer__page-error">
            <Icon name="info" />
            <span>{error}</span>
          </div>
        ) : null}

        {renderState === "ready" ? (
          <>
            <div
              className="pdf-viewer__text-layer"
              aria-hidden="true"
            >
              {textSpans.map((item) => (
                <span
                  key={item.key}
                  className={`pdf-viewer__text-item ${
                    item.matched ? "is-search-match" : ""
                  } ${
                    item.matched && activeSearchPage
                      ? "is-active-search-match"
                      : ""
                  }`}
                  style={item.style}
                >
                  {item.text}
                </span>
              ))}
            </div>

            <div
              className="pdf-viewer__annotation-layer"
              aria-label={`Links on page ${pageNumber}`}
            >
              {annotationLinks.map((annotation) => {
                const style = {
                  left: annotation.box.left,
                  top: annotation.box.top,
                  width: annotation.box.width,
                  height: annotation.box.height,
                };

                if (annotation.externalUrl) {
                  return (
                    <a
                      key={annotation.key}
                      className="pdf-viewer__annotation-link"
                      href={annotation.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={annotation.title}
                      style={style}
                      aria-label={annotation.title}
                    />
                  );
                }

                if (annotation.destination) {
                  return (
                    <button
                      key={annotation.key}
                      type="button"
                      className="pdf-viewer__annotation-link"
                      title={annotation.title}
                      style={style}
                      aria-label={annotation.title}
                      onClick={() =>
                        onNavigateDestination?.(
                          annotation.destination
                        )
                      }
                    />
                  );
                }

                return null;
              })}
            </div>
          </>
        ) : null}
      </div>

      <div className="pdf-viewer__page-number-label">
        {pageNumber}
      </div>
    </section>
  );
}

export default function PdfViewer({
  src,
  title = "",
  filename = "",
  downloadUrl = "",
  fallbackUrl = "",
  getBrowserFallbackUrl,
  httpHeaders,
  withCredentials = false,
  initialPage = 1,
  initialZoom = "fit-width",
  initialRotation = 0,
  defaultSidebarOpen = true,
  allowDownload = true,
  allowPrint = true,
  allowFullscreen = true,
  allowBrowserFallback = true,
  showTitle = true,
  className = "",
  style,
  cMapUrl = "",
  standardFontDataUrl = "",
  wasmUrl = "",
  onLoad,
  onError,
  onPageChange,
  onDownload,
  onPrint,
}) {
  const rootRef = useRef(null);
  const documentAreaRef = useRef(null);
  const pageRefs = useRef(new Map());
  const loadingTaskRef = useRef(null);
  const pdfDocumentRef = useRef(null);
  const cachedBlobRef = useRef(null);
  const browserFallbackObjectUrlsRef = useRef(new Set());
  const scrollFrameRef = useRef(null);
  const passwordCallbackRef = useRef(null);
  const searchGenerationRef = useRef(0);
  const searchInputRef = useRef(null);
  const moreMenuRef = useRef(null);
  const morePopupRef = useRef(null);
  const toolbarRef = useRef(null);
  const toolbarDragRef = useRef({
    pointerId: null,
    startX: 0,
    startScrollLeft: 0,
    moved: false,
  });
  const toolbarSuppressClickRef = useRef(false);
  const sourceKey = sourceToKey(src);

  const defaultZoomState = useMemo(() => {
    if (typeof initialZoom === "number") {
      return {
        mode: "custom",
        percent: clamp(
          Math.round(initialZoom),
          MIN_ZOOM,
          MAX_ZOOM
        ),
      };
    }

    return {
      mode:
        initialZoom === "fit-page"
          ? "fit-page"
          : "fit-width",
      percent: 100,
    };
  }, [initialZoom]);

  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [progress, setProgress] = useState({
    loaded: 0,
    total: 0,
    percent: 0,
  });
  const [pdfDocument, setPdfDocument] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(
    Math.max(1, Number(initialPage || 1))
  );
  const [pageDraft, setPageDraft] = useState(
    String(Math.max(1, Number(initialPage || 1)))
  );
  const [zoomMode, setZoomMode] = useState(
    defaultZoomState.mode
  );
  const [zoomPercent, setZoomPercent] = useState(
    defaultZoomState.percent
  );
  const [rotation, setRotation] = useState(
    normalizeRotation(initialRotation)
  );

  const [containerSize, setContainerSize] = useState({
    width: 900,
    height: 700,
  });
  const [firstPageSize, setFirstPageSize] = useState({
    width: 612,
    height: 792,
  });

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return defaultSidebarOpen;
    return window.innerWidth > 760 && defaultSidebarOpen;
  });
  const [sidebarTab, setSidebarTab] = useState("thumbnails");
  const [outline, setOutline] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [metadata, setMetadata] = useState(EMPTY_METADATA);

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchCaseSensitive, setSearchCaseSensitive] =
    useState(false);
  const [searchWholeWord, setSearchWholeWord] =
    useState(false);
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [activeSearchIndex, setActiveSearchIndex] =
    useState(-1);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [moreMenuPosition, setMoreMenuPosition] = useState({
    top: 0,
    left: 8,
    width: 220,
    ready: false,
  });
  const [toolbarOverflow, setToolbarOverflow] = useState({
    left: false,
    right: false,
  });
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState("");
  const [passwordState, setPasswordState] = useState({
    open: false,
    incorrect: false,
    busy: false,
  });

  const resolvedTitle =
    safeString(title) ||
    safeString(metadata.title) ||
    ensurePdfFilename(filename).replace(/\.pdf$/i, "");

  const resolvedFilename = ensurePdfFilename(
    filename || title || metadata.title || "document"
  );

  // A top-level browser navigation cannot attach a Firebase Bearer token.
  // Authenticated gateway sources therefore must use either:
  //   1) getBrowserFallbackUrl() -> opaque AskLawly browser ticket, or
  //   2) an authenticated fetch -> local Blob fallback.
  const hasAuthenticatedRequestHeaders = useMemo(
    () => hasAuthorizationHeader(httpHeaders),
    [httpHeaders]
  );

  const canUseBrowserFallback = useMemo(() => {
    if (!allowBrowserFallback) return false;

    const normalized = normalizeDocumentSource(src);

    return Boolean(
      typeof getBrowserFallbackUrl === "function" ||
        safeString(fallbackUrl) ||
        safeString(downloadUrl) ||
        normalized.url ||
        normalized.blob ||
        normalized.data ||
        pdfDocument
    );
  }, [
    allowBrowserFallback,
    getBrowserFallbackUrl,
    fallbackUrl,
    downloadUrl,
    src,
    sourceKey,
    pdfDocument,
  ]);

  const effectiveZoomPercent = useMemo(() => {
    const scale = resolveScale({
      mode: zoomMode,
      customPercent: zoomPercent,
      pageWidth: firstPageSize.width,
      pageHeight: firstPageSize.height,
      availableWidth: containerSize.width,
      availableHeight: containerSize.height,
    });

    return clamp(
      Math.round(scale * 100),
      MIN_ZOOM,
      MAX_ZOOM
    );
  }, [
    zoomMode,
    zoomPercent,
    firstPageSize.width,
    firstPageSize.height,
    containerSize.width,
    containerSize.height,
  ]);

  const activeSearchResult =
    activeSearchIndex >= 0
      ? searchResults[activeSearchIndex] || null
      : null;

  const visibleSearchLabel = searchTerm
    ? searchBusy
      ? "Searching…"
      : searchResults.length
      ? `${activeSearchIndex + 1} of ${searchResults.length}`
      : "No matches"
    : "";

  const loadDocument = useCallback(async () => {
    if (!src) {
      setStatus("error");
      setError("No PDF source was provided.");
      return;
    }

    setStatus("loading");
    setError("");
    setProgress({ loaded: 0, total: 0, percent: 0 });
    setPdfDocument(null);
    setNumPages(0);
    setOutline([]);
    setAttachments([]);
    setMetadata(EMPTY_METADATA);
    cachedBlobRef.current = null;

    try {
      await loadingTaskRef.current?.destroy?.();
    } catch {
      // Best-effort prior loading task cleanup.
    }

    try {
      await pdfDocumentRef.current?.destroy?.();
    } catch {
      // Best-effort prior document cleanup.
    }

    const normalized = normalizeDocumentSource(src);
    let params = createDocumentParams({
      src,
      httpHeaders,
      withCredentials,
      cMapUrl,
      standardFontDataUrl,
      wasmUrl,
    });

    if (normalized.blob) {
      cachedBlobRef.current = normalized.blob;
      const data = await normalized.blob.arrayBuffer();
      params = createDocumentParams({
        src: new Uint8Array(data),
        httpHeaders,
        withCredentials,
        cMapUrl,
        standardFontDataUrl,
        wasmUrl,
      });
    }

    const loadingTask = pdfjsLib.getDocument(params);
    loadingTaskRef.current = loadingTask;

    loadingTask.onProgress = ({ loaded, total }) => {
      const safeLoaded = Number(loaded || 0);
      const safeTotal = Number(total || 0);
      const percent =
        safeTotal > 0
          ? clamp(
              Math.round((safeLoaded / safeTotal) * 100),
              0,
              100
            )
          : 0;

      setProgress({
        loaded: safeLoaded,
        total: safeTotal,
        percent,
      });
    };

    loadingTask.onPassword = (updatePassword, reason) => {
      passwordCallbackRef.current = updatePassword;
      setPasswordState({
        open: true,
        incorrect:
          reason === pdfjsLib.PasswordResponses.INCORRECT_PASSWORD,
        busy: false,
      });
    };

    try {
      const loadedPdf = await loadingTask.promise;

      loadingTaskRef.current = null;
      pdfDocumentRef.current = loadedPdf;
      setPdfDocument(loadedPdf);
      setNumPages(loadedPdf.numPages);

      const safeInitialPage = clamp(
        Number(initialPage || 1),
        1,
        loadedPdf.numPages
      );
      setPageNumber(safeInitialPage);
      setPageDraft(String(safeInitialPage));

      const [
        metadataResult,
        outlineResult,
        attachmentsResult,
        firstPageResult,
      ] = await Promise.allSettled([
        loadedPdf.getMetadata(),
        loadedPdf.getOutline(),
        loadedPdf.getAttachments(),
        loadedPdf.getPage(1),
      ]);

      let nextMetadata = { ...EMPTY_METADATA };

      if (metadataResult.status === "fulfilled") {
        const info = metadataResult.value?.info || {};
        const metadataMap = metadataResult.value?.metadata;

        nextMetadata = {
          title:
            safeString(info.Title) ||
            safeString(metadataMap?.get?.("dc:title")),
          author:
            safeString(info.Author) ||
            safeString(metadataMap?.get?.("dc:creator")),
          subject: safeString(info.Subject),
          keywords: safeString(info.Keywords),
          creator: safeString(info.Creator),
          producer: safeString(info.Producer),
          creationDate: parsePdfDate(info.CreationDate),
          modificationDate: parsePdfDate(info.ModDate),
          pdfVersion: safeString(info.PDFFormatVersion),
          linearized:
            typeof info.IsLinearized === "boolean"
              ? info.IsLinearized
                ? "Yes"
                : "No"
              : "",
          tagged:
            typeof info.IsTagged === "boolean"
              ? info.IsTagged
                ? "Yes"
                : "No"
              : "",
          fileSize:
            normalized.blob?.size
              ? formatBytes(normalized.blob.size)
              : "",
        };
      }

      setMetadata(nextMetadata);

      if (
        outlineResult.status === "fulfilled" &&
        Array.isArray(outlineResult.value)
      ) {
        setOutline(outlineResult.value);
      }

      if (
        attachmentsResult.status === "fulfilled" &&
        attachmentsResult.value
      ) {
        setAttachments(
          Object.entries(attachmentsResult.value).map(
            ([name, attachment]) => ({
              name,
              filename:
                attachment?.filename ||
                attachment?.name ||
                name,
              content: attachment?.content || null,
              description: attachment?.description || "",
            })
          )
        );
      }

      if (firstPageResult.status === "fulfilled") {
        const viewport = firstPageResult.value.getViewport({
          scale: 1,
          rotation: normalizeRotation(initialRotation),
        });

        setFirstPageSize({
          width: viewport.width,
          height: viewport.height,
        });
      }

      setStatus("ready");

      onLoad?.({
        pdfDocument: loadedPdf,
        pages: loadedPdf.numPages,
        metadata: nextMetadata,
      });
    } catch (loadError) {
      if (loadError?.name === "PasswordException") {
        return;
      }

      console.error("[PdfViewer] document load failed:", loadError);
      const nextError =
        loadError instanceof Error
          ? loadError
          : new Error("The PDF could not be loaded.");

      setStatus("error");
      setError(
        nextError.message || "The PDF could not be loaded."
      );
      onError?.(nextError);
    }
  }, [
    src,
    sourceKey,
    httpHeaders,
    withCredentials,
    cMapUrl,
    standardFontDataUrl,
    wasmUrl,
    initialPage,
    initialRotation,
    onLoad,
    onError,
  ]);

  useEffect(() => {
    loadDocument();

    return () => {
      searchGenerationRef.current += 1;

      try {
        loadingTaskRef.current?.destroy?.();
      } catch {
        // Best-effort loading cleanup.
      }

      try {
        pdfDocumentRef.current?.destroy?.();
      } catch {
        // Best-effort PDF cleanup.
      }

      loadingTaskRef.current = null;
      pdfDocumentRef.current = null;
      cachedBlobRef.current = null;

      browserFallbackObjectUrlsRef.current.forEach((objectUrl) => {
        try {
          URL.revokeObjectURL(objectUrl);
        } catch {
          // Best-effort browser fallback URL cleanup.
        }
      });
      browserFallbackObjectUrlsRef.current.clear();
    };
  }, [loadDocument]);

  useEffect(() => {
    const node = documentAreaRef.current;
    if (!node) return undefined;

    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      setContainerSize({
        width: Math.max(320, rect.width),
        height: Math.max(360, rect.height),
      });
    };

    updateSize();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateSize);
      return () =>
        window.removeEventListener("resize", updateSize);
    }

    const observer = new ResizeObserver(updateSize);
    observer.observe(node);

    return () => observer.disconnect();
  }, [status, sidebarOpen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement === rootRef.current
      );
    };

    document.addEventListener(
      "fullscreenchange",
      handleFullscreenChange
    );

    return () => {
      document.removeEventListener(
        "fullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  const positionMoreMenu = useCallback(() => {
    const anchor = moreMenuRef.current;
    const popup = morePopupRef.current;
    if (!anchor || !popup) return;

    const viewportWidth =
      document.documentElement?.clientWidth || window.innerWidth || 0;
    const viewportHeight =
      document.documentElement?.clientHeight || window.innerHeight || 0;
    const margin = 8;
    const gap = 7;
    const width = Math.min(220, Math.max(160, viewportWidth - margin * 2));
    const anchorRect = anchor.getBoundingClientRect();
    const popupHeight = popup.getBoundingClientRect().height || 0;

    // Align the popup's right edge to the trigger so it expands to the left.
    const preferredLeft = anchorRect.right - width;
    const maxLeft = Math.max(margin, viewportWidth - width - margin);
    const left = clamp(preferredLeft, margin, maxLeft);

    let top = anchorRect.bottom + gap;
    const fitsBelow = top + popupHeight <= viewportHeight - margin;
    const fitsAbove = anchorRect.top - gap - popupHeight >= margin;

    if (!fitsBelow && fitsAbove) {
      top = anchorRect.top - gap - popupHeight;
    } else {
      top = clamp(
        top,
        margin,
        Math.max(margin, viewportHeight - popupHeight - margin)
      );
    }

    setMoreMenuPosition({
      top,
      left,
      width,
      ready: true,
    });
  }, []);

  useEffect(() => {
    if (!moreOpen) {
      setMoreMenuPosition((current) =>
        current.ready ? { ...current, ready: false } : current
      );
      return undefined;
    }

    const frame = window.requestAnimationFrame(positionMoreMenu);

    window.addEventListener("resize", positionMoreMenu);
    window.addEventListener("scroll", positionMoreMenu, true);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", positionMoreMenu);
      window.removeEventListener("scroll", positionMoreMenu, true);
    };
  }, [moreOpen, positionMoreMenu]);

  useEffect(() => {
    if (!moreOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!moreMenuRef.current?.contains(event.target)) {
        setMoreOpen(false);
      }
    };

    window.addEventListener("pointerdown", handlePointerDown);

    return () =>
      window.removeEventListener(
        "pointerdown",
        handlePointerDown
      );
  }, [moreOpen]);

  useEffect(() => {
    if (searchOpen) {
      window.setTimeout(() => {
        searchInputRef.current?.focus?.();
        searchInputRef.current?.select?.();
      }, 0);
    }
  }, [searchOpen]);

  const registerPage = useCallback((number, node) => {
    if (node) pageRefs.current.set(number, node);
    else pageRefs.current.delete(number);
  }, []);

  const scrollToPage = useCallback(
    (requestedPage, behavior = "smooth") => {
      const nextPage = clamp(
        Number(requestedPage || 1),
        1,
        Math.max(1, numPages)
      );
      const node = pageRefs.current.get(nextPage);

      if (node) {
        node.scrollIntoView({
          behavior,
          block: "start",
        });
      }

      setPageNumber(nextPage);
      setPageDraft(String(nextPage));
      onPageChange?.(nextPage);
    },
    [numPages, onPageChange]
  );

  const commitPageDraft = useCallback(() => {
    const parsed = Number.parseInt(pageDraft, 10);

    if (!Number.isFinite(parsed)) {
      setPageDraft(String(pageNumber));
      return;
    }

    scrollToPage(parsed);
  }, [pageDraft, pageNumber, scrollToPage]);

  const handleDocumentScroll = useCallback(() => {
    if (scrollFrameRef.current) return;

    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      const scroller = documentAreaRef.current;
      if (!scroller) return;

      const scrollerTop =
        scroller.getBoundingClientRect().top + 18;

      let closestPage = pageNumber;
      let closestDistance = Number.POSITIVE_INFINITY;

      pageRefs.current.forEach((node, number) => {
        if (!node) return;
        const rect = node.getBoundingClientRect();
        const distance = Math.abs(rect.top - scrollerTop);

        if (
          rect.bottom >= scrollerTop &&
          distance < closestDistance
        ) {
          closestDistance = distance;
          closestPage = number;
        }
      });

      if (closestPage !== pageNumber) {
        setPageNumber(closestPage);
        setPageDraft(String(closestPage));
        onPageChange?.(closestPage);
      }
    });
  }, [pageNumber, onPageChange]);

  useEffect(() => {
    if (!pdfDocument || !searchTerm.trim()) {
      searchGenerationRef.current += 1;
      setSearchResults([]);
      setActiveSearchIndex(-1);
      setSearchBusy(false);
      return undefined;
    }

    const generation = searchGenerationRef.current + 1;
    searchGenerationRef.current = generation;
    setSearchBusy(true);

    let cancelled = false;

    (async () => {
      const regex = buildSearchRegex(
        searchTerm.trim(),
        searchCaseSensitive,
        searchWholeWord
      );
      const results = [];

      for (
        let number = 1;
        number <= pdfDocument.numPages;
        number += 1
      ) {
        if (
          cancelled ||
          searchGenerationRef.current !== generation
        ) {
          return;
        }

        const page = await pdfDocument.getPage(number);
        const textContent = await page.getTextContent({
          disableNormalization: false,
        });
        const pageText = (textContent?.items || [])
          .map((item) => item?.str || "")
          .join(" ")
          .replace(/\s+/g, " ")
          .trim();

        const matches = countMatches(pageText, regex);

        for (let matchIndex = 0; matchIndex < matches; matchIndex += 1) {
          results.push({
            pageNumber: number,
            matchIndex,
          });
        }

        if (number % SEARCH_BATCH_YIELD === 0) {
          await new Promise((resolve) =>
            window.setTimeout(resolve, 0)
          );
        }
      }

      if (
        cancelled ||
        searchGenerationRef.current !== generation
      ) {
        return;
      }

      setSearchResults(results);
      setSearchBusy(false);

      if (results.length) {
        setActiveSearchIndex(0);
        scrollToPage(results[0].pageNumber);
      } else {
        setActiveSearchIndex(-1);
      }
    })().catch((searchError) => {
      if (!cancelled) {
        console.error("[PdfViewer] search failed:", searchError);
        setSearchResults([]);
        setActiveSearchIndex(-1);
        setSearchBusy(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    pdfDocument,
    searchTerm,
    searchCaseSensitive,
    searchWholeWord,
    scrollToPage,
  ]);

  const moveSearch = useCallback(
    (direction) => {
      if (!searchResults.length) return;

      const nextIndex =
        (activeSearchIndex + direction + searchResults.length) %
        searchResults.length;

      setActiveSearchIndex(nextIndex);
      scrollToPage(searchResults[nextIndex].pageNumber);
    },
    [searchResults, activeSearchIndex, scrollToPage]
  );

  const navigateDestination = useCallback(
    async (destination) => {
      if (!pdfDocument || !destination) return;

      try {
        const explicitDestination =
          typeof destination === "string"
            ? await pdfDocument.getDestination(destination)
            : destination;

        if (
          !Array.isArray(explicitDestination) ||
          explicitDestination.length === 0
        ) {
          return;
        }

        const pageReference = explicitDestination[0];
        const pageIndex =
          typeof pageReference === "object"
            ? await pdfDocument.getPageIndex(pageReference)
            : Number(pageReference);

        if (Number.isFinite(pageIndex)) {
          scrollToPage(pageIndex + 1);
        }
      } catch (navigationError) {
        console.error(
          "[PdfViewer] destination navigation failed:",
          navigationError
        );
      }
    },
    [pdfDocument, scrollToPage]
  );

  const setCustomZoom = useCallback((percent) => {
    setZoomMode("custom");
    setZoomPercent(
      clamp(Math.round(percent), MIN_ZOOM, MAX_ZOOM)
    );
  }, []);

  const zoomOut = useCallback(() => {
    setCustomZoom(effectiveZoomPercent - ZOOM_STEP);
  }, [effectiveZoomPercent, setCustomZoom]);

  const zoomIn = useCallback(() => {
    setCustomZoom(effectiveZoomPercent + ZOOM_STEP);
  }, [effectiveZoomPercent, setCustomZoom]);

  const handleZoomSelect = useCallback(
    (value) => {
      if (value === "fit-width" || value === "fit-page") {
        setZoomMode(value);
        return;
      }

      const match = String(value).match(/^custom:(\d+)$/);
      if (match) setCustomZoom(Number(match[1]));
    },
    [setCustomZoom]
  );

  const rotateClockwise = useCallback(() => {
    setRotation((value) => normalizeRotation(value + 90));
  }, []);

  const getPdfBlob = useCallback(async () => {
    if (cachedBlobRef.current) {
      return cachedBlobRef.current;
    }

    const normalized = normalizeDocumentSource(src);
    const typedBlob =
      normalized.data && makeBlobFromTypedData(normalized.data);

    if (typedBlob) {
      cachedBlobRef.current = typedBlob;
      return typedBlob;
    }

    const preferredUrl =
      safeString(downloadUrl) ||
      safeString(normalized.url);

    if (preferredUrl) {
      try {
        const response = await fetch(preferredUrl, {
          method: "GET",
          headers: httpHeaders || undefined,
          credentials: withCredentials ? "include" : "omit",
          mode: "cors",
        });

        if (!response.ok) {
          throw new Error(`PDF request failed (${response.status}).`);
        }

        const blob = await response.blob();

        if (
          blob.type &&
          !/application\/pdf/i.test(blob.type)
        ) {
          console.warn(
            "[PdfViewer] downloaded blob did not advertise application/pdf:",
            blob.type
          );
        }

        cachedBlobRef.current = blob;
        return blob;
      } catch (fetchError) {
        console.warn(
          "[PdfViewer] direct PDF fetch failed; falling back to PDF.js data:",
          fetchError
        );
      }
    }

    if (pdfDocument) {
      const bytes = await pdfDocument.getData();
      const blob = new Blob([bytes], {
        type: "application/pdf",
      });

      cachedBlobRef.current = blob;
      return blob;
    }

    throw new Error("No downloadable PDF data is available.");
  }, [
    src,
    downloadUrl,
    httpHeaders,
    withCredentials,
    pdfDocument,
  ]);

  const handleDownload = useCallback(async () => {
    if (!allowDownload || actionBusy) return;

    setActionBusy("download");

    try {
      const blob = await getPdfBlob();
      triggerBlobDownload(blob, resolvedFilename);
      await onDownload?.({
        blob,
        filename: resolvedFilename,
      });
    } catch (downloadError) {
      console.error("[PdfViewer] download failed:", downloadError);

      // Legacy/public URLs may still use direct navigation as a last resort.
      // Authorization-bound File Access Gateway URLs must never do this because
      // a new browser navigation cannot carry the Firebase Bearer header.
      const directUrl = hasAuthenticatedRequestHeaders
        ? ""
        : safeString(downloadUrl) ||
          safeString(normalizeDocumentSource(src).url);

      if (directUrl) {
        const anchor = document.createElement("a");
        anchor.href = directUrl;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      } else {
        window.alert(
          downloadError?.message || "The PDF could not be downloaded."
        );
      }
    } finally {
      setActionBusy("");
    }
  }, [
    allowDownload,
    actionBusy,
    getPdfBlob,
    resolvedFilename,
    onDownload,
    downloadUrl,
    src,
    hasAuthenticatedRequestHeaders,
  ]);

  const handlePrint = useCallback(async () => {
    if (!allowPrint || actionBusy) return;

    setActionBusy("print");

    try {
      const blob = await getPdfBlob();
      await onPrint?.({
        blob,
        filename: resolvedFilename,
      });

      const objectUrl = URL.createObjectURL(blob);
      const isMobileSafari =
        /iP(ad|hone|od)/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" &&
          navigator.maxTouchPoints > 1);

      if (isMobileSafari) {
        const printWindow = window.open(
          objectUrl,
          "_blank",
          "noopener,noreferrer"
        );

        if (!printWindow) {
          URL.revokeObjectURL(objectUrl);
          throw new Error(
            "The print window was blocked. Please allow pop-ups and try again."
          );
        }

        window.setTimeout(() => {
          URL.revokeObjectURL(objectUrl);
        }, OBJECT_URL_CLEANUP_MS);

        return;
      }

      const frame = document.createElement("iframe");
      frame.style.position = "fixed";
      frame.style.right = "0";
      frame.style.bottom = "0";
      frame.style.width = "1px";
      frame.style.height = "1px";
      frame.style.border = "0";
      frame.style.opacity = "0";
      frame.setAttribute("aria-hidden", "true");
      frame.src = objectUrl;

      const cleanup = () => {
        try {
          frame.remove();
        } catch {
          // Best-effort print frame cleanup.
        }

        try {
          URL.revokeObjectURL(objectUrl);
        } catch {
          // Best-effort print URL cleanup.
        }
      };

      frame.onload = () => {
        window.setTimeout(() => {
          try {
            frame.contentWindow?.focus?.();
            frame.contentWindow?.print?.();
          } catch (printError) {
            console.error(
              "[PdfViewer] iframe printing failed:",
              printError
            );
            window.open(
              objectUrl,
              "_blank",
              "noopener,noreferrer"
            );
          }
        }, 250);
      };

      document.body.appendChild(frame);
      window.setTimeout(cleanup, PRINT_CLEANUP_MS);
    } catch (printError) {
      console.error("[PdfViewer] print failed:", printError);
      window.alert(
        printError?.message || "The PDF could not be printed."
      );
    } finally {
      setActionBusy("");
    }
  }, [
    allowPrint,
    actionBusy,
    getPdfBlob,
    onPrint,
    resolvedFilename,
  ]);

  const toggleFullscreen = useCallback(async () => {
    if (!allowFullscreen) return;

    try {
      if (document.fullscreenElement === rootRef.current) {
        await document.exitFullscreen?.();
      } else {
        await rootRef.current?.requestFullscreen?.();
      }
    } catch (fullscreenError) {
      console.error(
        "[PdfViewer] fullscreen request failed:",
        fullscreenError
      );
    }
  }, [allowFullscreen]);

  const openBrowserFallback = useCallback(async () => {
    if (!canUseBrowserFallback || actionBusy) return;

    // The blank destination must be opened synchronously from the user's click.
    // Otherwise mobile browsers and pop-up blockers may reject the later
    // navigation after the signed-URL request or Blob preparation finishes.
    const browserWindow = window.open("", "_blank");

    if (!browserWindow) {
      window.alert(
        "The browser blocked the PDF window. Please allow pop-ups for AskLawly and try again."
      );
      return;
    }

    prepareBrowserFallbackWindow(browserWindow, resolvedTitle);
    setActionBusy("browser");

    let callbackError = null;
    let objectUrl = "";

    try {
      let browserUrl = "";

      // Preferred path: ask the parent for a browser-openable URL at click time.
      // File Access Gateway callers return an opaque AskLawly browser ticket;
      // legacy callers may still return a fresh public/signed URL.
      if (typeof getBrowserFallbackUrl === "function") {
        try {
          const result = await getBrowserFallbackUrl({
            src,
            title: resolvedTitle,
            filename: resolvedFilename,
            pdfDocument,
          });
          browserUrl = fallbackResultUrl(result);
        } catch (error) {
          callbackError = error;
          console.warn(
            "[PdfViewer] browser fallback URL request failed; trying local PDF bytes:",
            error
          );
        }
      }

      // Direct URL navigation is allowed only for sources that are already
      // browser-openable without an Authorization header. For the private File
      // Access Gateway, the opaque ticket provider above is authoritative.
      if (!browserUrl && !hasAuthenticatedRequestHeaders) {
        browserUrl =
          safeString(fallbackUrl) ||
          safeString(downloadUrl) ||
          safeString(normalizeDocumentSource(src).url);
      }

      // Blob, File, ArrayBuffer, Uint8Array, authenticated gateway PDFs, and
      // already-loaded protected PDFs can fall back using local PDF bytes.
      if (!browserUrl || !isBrowserOpenablePdfUrl(browserUrl)) {
        const blob = await getPdfBlob();
        objectUrl = URL.createObjectURL(blob);
        browserFallbackObjectUrlsRef.current.add(objectUrl);
        browserUrl = objectUrl;
      }

      if (!isBrowserOpenablePdfUrl(browserUrl)) {
        throw (
          callbackError ||
          new Error(
            "No browser-compatible PDF source is available."
          )
        );
      }

      try {
        browserWindow.location.replace(browserUrl);
      } catch {
        browserWindow.location.href = browserUrl;
      }

      if (objectUrl) {
        window.setTimeout(() => {
          try {
            URL.revokeObjectURL(objectUrl);
          } catch {
            // Best-effort delayed fallback URL cleanup.
          }
          browserFallbackObjectUrlsRef.current.delete(objectUrl);
        }, OBJECT_URL_CLEANUP_MS);
      }
    } catch (fallbackError) {
      try {
        browserWindow.close();
      } catch {
        // Best-effort temporary-window cleanup.
      }

      console.error(
        "[PdfViewer] browser fallback failed:",
        fallbackError
      );

      window.alert(
        fallbackError?.message ||
          callbackError?.message ||
          "The PDF could not be opened with the browser viewer."
      );
    } finally {
      setActionBusy("");
    }
  }, [
    canUseBrowserFallback,
    actionBusy,
    getBrowserFallbackUrl,
    src,
    resolvedTitle,
    resolvedFilename,
    pdfDocument,
    fallbackUrl,
    downloadUrl,
    getPdfBlob,
    hasAuthenticatedRequestHeaders,
  ]);

  const downloadAttachment = useCallback((attachment) => {
    if (!attachment?.content) return;

    const blob = new Blob([attachment.content], {
      type: "application/octet-stream",
    });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = objectUrl;
    anchor.download =
      safeString(attachment.filename) ||
      safeString(attachment.name) ||
      "attachment";
    anchor.style.display = "none";

    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    window.setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, 1500);
  }, []);

  const submitPassword = useCallback((password) => {
    const callback = passwordCallbackRef.current;
    if (!callback || !password) return;

    setPasswordState((state) => ({
      ...state,
      busy: true,
    }));

    callback(password);

    window.setTimeout(() => {
      setPasswordState((state) => ({
        ...state,
        busy: false,
      }));
    }, 400);
  }, []);

  const cancelPassword = useCallback(() => {
    passwordCallbackRef.current = null;
    setPasswordState({
      open: false,
      incorrect: false,
      busy: false,
    });

    try {
      loadingTaskRef.current?.destroy?.();
    } catch {
      // Best-effort password cancellation.
    }

    setStatus("error");
    setError("The password-protected PDF was not opened.");
  }, []);

  useEffect(() => {
    if (status === "ready") {
      setPasswordState({
        open: false,
        incorrect: false,
        busy: false,
      });
      passwordCallbackRef.current = null;
    }
  }, [status]);

  const updateToolbarOverflow = useCallback(() => {
    const toolbar = toolbarRef.current;

    if (!toolbar) {
      setToolbarOverflow((current) =>
        current.left || current.right
          ? { left: false, right: false }
          : current
      );
      return;
    }

    const maxScrollLeft = Math.max(
      0,
      toolbar.scrollWidth - toolbar.clientWidth
    );
    const next = {
      left: toolbar.scrollLeft > 2,
      right: toolbar.scrollLeft < maxScrollLeft - 2,
    };

    setToolbarOverflow((current) =>
      current.left === next.left && current.right === next.right
        ? current
        : next
    );
  }, []);

  useEffect(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return undefined;

    let frame = window.requestAnimationFrame(updateToolbarOverflow);
    const centerTrack = toolbar.querySelector(
      ".pdf-viewer__toolbar-center-track"
    );
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(updateToolbarOverflow);
          })
        : null;

    observer?.observe(toolbar);
    if (centerTrack) observer?.observe(centerTrack);
    window.addEventListener("resize", updateToolbarOverflow);

    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", updateToolbarOverflow);
    };
  }, [
    updateToolbarOverflow,
    allowPrint,
    allowDownload,
    allowFullscreen,
    showTitle,
    resolvedTitle,
  ]);

  const scrollToolbarControls = useCallback(
    (direction) => {
      const toolbar = toolbarRef.current;
      if (!toolbar) return;

      const distance = Math.max(
        180,
        Math.min(260, Math.round(toolbar.clientWidth * 0.72))
      );

      toolbar.scrollBy({
        left: direction * distance,
        behavior: "smooth",
      });

      window.setTimeout(updateToolbarOverflow, 260);
    },
    [updateToolbarOverflow]
  );

  const handleToolbarPointerDown = useCallback((event) => {
    if (
      event.pointerType !== "mouse" ||
      event.button !== 0
    ) {
      return;
    }

    const toolbar = toolbarRef.current;
    if (
      !toolbar ||
      toolbar.scrollWidth <= toolbar.clientWidth + 1
    ) {
      return;
    }

    // Interactive controls must keep their normal click/tap behavior.
    // Horizontal toolbar dragging may start only from non-interactive toolbar
    // surface, never from Print, Download, navigation, search, zoom, links,
    // form controls, or other actionable elements.
    if (
      event.target?.closest?.(
        "button, a, input, select, option, textarea, label, [role='button'], [contenteditable='true']"
      )
    ) {
      return;
    }

    toolbarDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: toolbar.scrollLeft,
      moved: false,
    };

    toolbar.dataset.dragging = "false";
    toolbar.setPointerCapture?.(
      event.pointerId
    );
  }, []);

  const handleToolbarPointerMove = useCallback((event) => {
    const toolbar = toolbarRef.current;
    const drag = toolbarDragRef.current;

    if (
      !toolbar ||
      drag.pointerId !== event.pointerId
    ) {
      return;
    }

    const deltaX =
      event.clientX - drag.startX;

    if (
      !drag.moved &&
      Math.abs(deltaX) < 4
    ) {
      return;
    }

    drag.moved = true;
    toolbarSuppressClickRef.current = true;
    toolbar.dataset.dragging = "true";

    event.preventDefault();
    toolbar.scrollLeft =
      drag.startScrollLeft - deltaX;
  }, []);

  const finishToolbarPointerDrag = useCallback((event) => {
    const toolbar = toolbarRef.current;
    const drag = toolbarDragRef.current;

    if (
      !toolbar ||
      drag.pointerId !== event.pointerId
    ) {
      return;
    }

    const moved = drag.moved;

    try {
      toolbar.releasePointerCapture?.(
        event.pointerId
      );
    } catch {
      // The browser may already have released pointer capture.
    }

    toolbar.dataset.dragging = "false";
    toolbarDragRef.current = {
      pointerId: null,
      startX: 0,
      startScrollLeft: toolbar.scrollLeft,
      moved: false,
    };

    if (moved) {
      toolbarSuppressClickRef.current = true;

      window.setTimeout(() => {
        toolbarSuppressClickRef.current = false;
      }, 80);
    }
  }, []);

  const handleToolbarClickCapture = useCallback((event) => {
    if (!toolbarSuppressClickRef.current) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      const commandKey = event.ctrlKey || event.metaKey;

      if (commandKey && event.key.toLowerCase() === "f") {
        event.preventDefault();
        setSearchOpen(true);
        return;
      }

      if (commandKey && event.key.toLowerCase() === "p") {
        event.preventDefault();
        handlePrint();
        return;
      }

      if (commandKey && event.key.toLowerCase() === "s") {
        event.preventDefault();
        handleDownload();
        return;
      }

      if (isEditableTarget(event.target)) {
        if (
          event.key === "Enter" &&
          event.target === searchInputRef.current
        ) {
          event.preventDefault();
          moveSearch(event.shiftKey ? -1 : 1);
        }
        return;
      }

      if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        zoomIn();
        return;
      }

      if (event.key === "-") {
        event.preventDefault();
        zoomOut();
        return;
      }

      if (commandKey && event.key === "0") {
        event.preventDefault();
        setCustomZoom(100);
        return;
      }

      if (event.key === "PageDown") {
        event.preventDefault();
        scrollToPage(pageNumber + 1);
        return;
      }

      if (event.key === "PageUp") {
        event.preventDefault();
        scrollToPage(pageNumber - 1);
        return;
      }

      if (event.key === "Home" && commandKey) {
        event.preventDefault();
        scrollToPage(1);
        return;
      }

      if (event.key === "End" && commandKey) {
        event.preventDefault();
        scrollToPage(numPages);
        return;
      }

      if (event.key === "Escape") {
        setMoreOpen(false);
        setPropertiesOpen(false);

        if (searchOpen) {
          setSearchOpen(false);
          return;
        }

        if (
          window.innerWidth <= 760 &&
          sidebarOpen
        ) {
          setSidebarOpen(false);
        }
      }
    },
    [
      handlePrint,
      handleDownload,
      moveSearch,
      zoomIn,
      zoomOut,
      setCustomZoom,
      scrollToPage,
      pageNumber,
      numPages,
      searchOpen,
      sidebarOpen,
    ]
  );

  const zoomSelectValue =
    zoomMode === "custom"
      ? ZOOM_OPTIONS.some(
          (option) =>
            option.value === `custom:${zoomPercent}`
        )
        ? `custom:${zoomPercent}`
        : `custom:${effectiveZoomPercent}`
      : zoomMode;

  const metadataRows = [
    ["Title", metadata.title],
    ["Author", metadata.author],
    ["Subject", metadata.subject],
    ["Keywords", metadata.keywords],
    ["Creator", metadata.creator],
    ["Producer", metadata.producer],
    ["Created", metadata.creationDate],
    ["Modified", metadata.modificationDate],
    ["PDF version", metadata.pdfVersion],
    ["Pages", numPages ? String(numPages) : ""],
    ["File size", metadata.fileSize],
    ["Fast web view", metadata.linearized],
    ["Tagged PDF", metadata.tagged],
  ].filter(([, value]) => safeString(value));

  return (
    <div
      ref={rootRef}
      className={`pdf-viewer ${className}`}
      style={style}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      data-status={status}
      aria-label="PDF Viewer"
    >
      <style>{`
        .pdf-viewer {
          --pdf-brand: ${BRAND};
          --pdf-toolbar-bg: ${TOOLBAR_BG};
          --pdf-toolbar-border: ${TOOLBAR_BORDER};
          --pdf-viewer-bg: ${VIEWER_BG};
          --pdf-sidebar-bg: ${SIDEBAR_BG};
          --pdf-sidebar-border: ${SIDEBAR_BORDER};
          position: relative;
          width: 100%;
          height: 100%;
          min-height: 460px;
          display: grid;
          grid-template-rows: auto auto minmax(0, 1fr);
          overflow: hidden;
          background: var(--pdf-viewer-bg);
          color: ${TEXT_LIGHT};
          font-family: Arial, Helvetica, sans-serif;
          outline: none;
          isolation: isolate;
        }

        .pdf-viewer:fullscreen {
          width: 100vw;
          height: 100vh;
          min-height: 100vh;
          background: var(--pdf-viewer-bg);
        }

        .pdf-viewer button,
        .pdf-viewer input,
        .pdf-viewer select {
          font: inherit;
        }

        .pdf-viewer__toolbar {
          position: relative;
          min-height: 54px;
          display: block;
          overflow: visible;
          border-bottom: 1px solid var(--pdf-toolbar-border);
          background: var(--pdf-toolbar-bg);
          color: ${TEXT_LIGHT};
          box-sizing: border-box;
          z-index: 20;
        }

        .pdf-viewer__toolbar-track {
          width: 100%;
          min-width: 0;
          min-height: 54px;
          display: grid;
          grid-template-columns:
            minmax(150px, 240px)
            minmax(0, 1fr)
            auto;
          align-items: center;
          gap: 8px;
          padding: 7px 10px;
          box-sizing: border-box;
        }

        .pdf-viewer__toolbar-section {
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: nowrap;
        }

        .pdf-viewer__toolbar-section.is-left {
          overflow: hidden;
        }

        .pdf-viewer__toolbar-section.is-right {
          flex: 0 0 auto;
          justify-content: flex-end;
        }

        .pdf-viewer__toolbar-center-shell {
          position: relative;
          min-width: 0;
          height: 40px;
          display: flex;
          align-items: center;
          overflow: hidden;
        }

        .pdf-viewer__toolbar-center-scroll {
          width: 100%;
          min-width: 0;
          height: 40px;
          overflow-x: auto;
          overflow-y: hidden;
          cursor: grab;
          scroll-behavior: smooth;
          overscroll-behavior-x: contain;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          touch-action: pan-x;
        }

        .pdf-viewer__toolbar-center-scroll::-webkit-scrollbar {
          display: none;
        }

        .pdf-viewer__toolbar-center-scroll[data-dragging="true"] {
          cursor: grabbing;
          scroll-behavior: auto;
          user-select: none;
        }

        .pdf-viewer__toolbar-center-track {
          width: max-content;
          min-width: 100%;
          min-height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 0 30px;
          box-sizing: border-box;
          white-space: nowrap;
          flex-wrap: nowrap;
        }

        .pdf-viewer__toolbar-center-track > * {
          flex: 0 0 auto;
        }

        .pdf-viewer__toolbar-scroll-button {
          appearance: none;
          position: absolute;
          top: 50%;
          z-index: 4;
          width: 30px;
          height: 38px;
          padding: 0;
          border: 0;
          color: #FFFFFF;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transform: translateY(-50%);
          cursor: pointer;
          opacity: .84;
          transition:
            opacity .15s ease,
            background .15s ease;
          -webkit-tap-highlight-color: transparent;
        }

        .pdf-viewer__toolbar-scroll-button.is-left {
          left: 0;
          background:
            linear-gradient(
              90deg,
              var(--pdf-toolbar-bg) 0%,
              rgba(52,52,52,.96) 64%,
              rgba(52,52,52,0) 100%
            );
        }

        .pdf-viewer__toolbar-scroll-button.is-right {
          right: 0;
          background:
            linear-gradient(
              270deg,
              var(--pdf-toolbar-bg) 0%,
              rgba(52,52,52,.96) 64%,
              rgba(52,52,52,0) 100%
            );
        }

        .pdf-viewer__toolbar-center-shell:hover
          .pdf-viewer__toolbar-scroll-button,
        .pdf-viewer__toolbar-center-shell:focus-within
          .pdf-viewer__toolbar-scroll-button,
        .pdf-viewer__toolbar-scroll-button:hover,
        .pdf-viewer__toolbar-scroll-button:focus-visible {
          opacity: 1;
          outline: none;
        }

        .pdf-viewer__title {
          min-width: 0;
          margin-left: 8px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #FFFFFF;
          font-size: 13px;
          line-height: 1.2;
          font-weight: 800;
        }

        .pdf-viewer__toolbar-button {
          appearance: none;
          min-width: 36px;
          height: 36px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          padding: 0 10px;
          border: 1px solid transparent;
          border-radius: 7px;
          background: transparent;
          color: ${TEXT_LIGHT};
          cursor: pointer;
          transition:
            background .15s ease,
            border-color .15s ease,
            color .15s ease,
            transform .06s ease;
          white-space: nowrap;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
        }

        .pdf-viewer__toolbar-button.is-compact {
          width: 36px;
          padding: 0;
        }

        .pdf-viewer__toolbar-button:hover:not(:disabled),
        .pdf-viewer__toolbar-button:focus-visible {
          background: rgba(255, 255, 255, 0.11);
          border-color: rgba(255, 255, 255, 0.12);
          outline: none;
        }

        .pdf-viewer__toolbar-button:active:not(:disabled) {
          transform: translateY(1px);
        }

        .pdf-viewer__toolbar-button.is-active {
          background: rgba(255, 255, 255, 0.17);
          border-color: rgba(255, 255, 255, 0.20);
        }

        .pdf-viewer__toolbar-button:disabled {
          cursor: not-allowed;
          opacity: .42;
        }

        .pdf-viewer__toolbar-divider {
          width: 1px;
          height: 25px;
          margin: 0 4px;
          background: rgba(255, 255, 255, 0.22);
        }

        .pdf-viewer__page-control {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          color: #FFFFFF;
          font-size: 12px;
          font-weight: 750;
        }

        .pdf-viewer__page-input {
          width: 46px;
          height: 30px;
          padding: 0 6px;
          border: 1px solid #1F1F1F;
          border-radius: 3px;
          background: #191919;
          color: #FFFFFF;
          text-align: center;
          font-size: 13px;
          font-weight: 800;
          box-sizing: border-box;
          outline: none;
        }

        .pdf-viewer__page-input:focus {
          border-color: #93C5FD;
          box-shadow: 0 0 0 2px rgba(147, 197, 253, .18);
        }

        .pdf-viewer__zoom-select {
          height: 32px;
          min-width: 92px;
          padding: 0 24px 0 9px;
          border: 1px solid #1F1F1F;
          border-radius: 4px;
          background: #191919;
          color: #FFFFFF;
          cursor: pointer;
          font-size: 12px;
          font-weight: 800;
          outline: none;
        }

        .pdf-viewer__search-bar {
          min-height: 48px;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 7px 10px;
          border-bottom: 1px solid #D7DEE8;
          background: #FFFFFF;
          color: #0F172A;
          box-sizing: border-box;
          z-index: 19;
        }

        .pdf-viewer__search-input-wrap {
          min-width: 180px;
          flex: 1;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0 10px;
          border: 1px solid #CBD5E1;
          border-radius: 8px;
          background: #FFFFFF;
        }

        .pdf-viewer__search-input {
          min-width: 0;
          width: 100%;
          height: 34px;
          padding: 0;
          border: 0;
          outline: none;
          background: transparent;
          color: #0F172A;
          font-size: 13px;
        }

        .pdf-viewer__search-count {
          min-width: 74px;
          color: #64748B;
          font-size: 11px;
          font-weight: 750;
          text-align: center;
          white-space: nowrap;
        }

        .pdf-viewer__search-option {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          color: #334155;
          font-size: 11px;
          font-weight: 750;
          white-space: nowrap;
          cursor: pointer;
        }

        .pdf-viewer__search-option input {
          accent-color: var(--pdf-brand);
        }

        .pdf-viewer__search-action {
          width: 34px;
          height: 34px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #CBD5E1;
          border-radius: 8px;
          background: #FFFFFF;
          color: #334155;
          cursor: pointer;
        }

        .pdf-viewer__search-action:disabled {
          cursor: not-allowed;
          opacity: .45;
        }

        .pdf-viewer__body {
          min-height: 0;
          position: relative;
          display: flex;
          overflow: hidden;
          background: var(--pdf-viewer-bg);
        }

        .pdf-viewer__sidebar {
          width: 238px;
          min-width: 238px;
          height: 100%;
          display: grid;
          grid-template-rows: auto minmax(0, 1fr);
          border-right: 1px solid var(--pdf-sidebar-border);
          background: var(--pdf-sidebar-bg);
          color: ${TEXT_LIGHT};
          overflow: hidden;
          z-index: 15;
        }

        .pdf-viewer__sidebar-tabs {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 4px;
          padding: 7px;
          border-bottom: 1px solid var(--pdf-sidebar-border);
          background: #303030;
        }

        .pdf-viewer__sidebar-tab {
          appearance: none;
          height: 36px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid transparent;
          border-radius: 7px;
          background: transparent;
          color: ${TEXT_MUTED};
          cursor: pointer;
        }

        .pdf-viewer__sidebar-tab:hover,
        .pdf-viewer__sidebar-tab.is-active {
          border-color: rgba(255,255,255,.13);
          background: rgba(255,255,255,.10);
          color: #FFFFFF;
        }

        .pdf-viewer__sidebar-content {
          min-height: 0;
          overflow: auto;
          overscroll-behavior: contain;
          scrollbar-color: #6B7280 transparent;
        }

        .pdf-viewer__thumbnail-list {
          display: grid;
          justify-items: center;
          gap: 16px;
          padding: 16px 10px 28px;
        }

        .pdf-viewer__thumbnail {
          appearance: none;
          width: 178px;
          display: grid;
          justify-items: center;
          gap: 7px;
          padding: 8px;
          border: 2px solid transparent;
          border-radius: 8px;
          background: transparent;
          color: #E2E8F0;
          cursor: pointer;
        }

        .pdf-viewer__thumbnail:hover {
          background: rgba(255,255,255,.06);
        }

        .pdf-viewer__thumbnail.is-active {
          border-color: #60A5FA;
          background: rgba(96,165,250,.12);
        }

        .pdf-viewer__thumbnail-paper {
          display: grid;
          place-items: center;
          overflow: hidden;
          background: #FFFFFF;
          box-shadow: 0 3px 13px rgba(0,0,0,.35);
        }

        .pdf-viewer__thumbnail-paper canvas {
          display: block;
        }

        .pdf-viewer__thumbnail > span {
          font-size: 11px;
          font-weight: 750;
        }

        .pdf-viewer__outline-tree {
          display: grid;
          padding: 7px 0 20px;
        }

        .pdf-viewer__outline-link {
          appearance: none;
          width: 100%;
          min-height: 34px;
          padding-top: 7px;
          padding-right: 10px;
          padding-bottom: 7px;
          border: 0;
          background: transparent;
          color: #E2E8F0;
          cursor: pointer;
          overflow: hidden;
          text-align: left;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 12px;
        }

        .pdf-viewer__outline-link:hover {
          background: rgba(255,255,255,.08);
          color: #FFFFFF;
        }

        .pdf-viewer__attachments {
          display: grid;
          gap: 8px;
          padding: 12px;
        }

        .pdf-viewer__attachment {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
          gap: 8px;
          padding: 9px 10px;
          border: 1px solid rgba(255,255,255,.10);
          border-radius: 8px;
          background: rgba(255,255,255,.04);
        }

        .pdf-viewer__attachment-name {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #F8FAFC;
          font-size: 11px;
          font-weight: 750;
        }

        .pdf-viewer__attachment button {
          appearance: none;
          width: 30px;
          height: 30px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(255,255,255,.14);
          border-radius: 7px;
          background: rgba(255,255,255,.07);
          color: #FFFFFF;
          cursor: pointer;
        }

        .pdf-viewer__sidebar-empty {
          padding: 24px 14px;
          color: #94A3B8;
          text-align: center;
          font-size: 12px;
          line-height: 1.5;
        }

        .pdf-viewer__document-scroll {
          min-width: 0;
          min-height: 0;
          flex: 1;
          position: relative;
          overflow: auto;
          overscroll-behavior: contain;
          background: var(--pdf-viewer-bg);
          scrollbar-color: #737373 #2A2A2A;
          scrollbar-width: auto;
        }

        .pdf-viewer__document {
          min-width: max-content;
          min-height: 100%;
          display: grid;
          justify-items: center;
          align-content: start;
          gap: 18px;
          padding: 18px 30px 46px;
          box-sizing: border-box;
        }

        .pdf-viewer__page-shell {
          position: relative;
          display: grid;
          justify-items: center;
          scroll-margin-top: 18px;
        }

        .pdf-viewer__page-paper {
          position: relative;
          overflow: hidden;
          background: #FFFFFF;
          box-shadow:
            0 1px 3px rgba(0,0,0,.45),
            0 10px 26px rgba(0,0,0,.22);
        }

        .pdf-viewer__page-shell.has-active-search .pdf-viewer__page-paper {
          box-shadow:
            0 0 0 2px rgba(249,115,22,.85),
            0 10px 26px rgba(0,0,0,.25);
        }

        .pdf-viewer__page-canvas {
          position: absolute;
          inset: 0;
          display: block;
          width: 100%;
          height: 100%;
          background: #FFFFFF;
        }

        .pdf-viewer__page-loading,
        .pdf-viewer__page-error {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          background: #FFFFFF;
          color: #64748B;
          text-align: center;
          font-size: 12px;
          font-weight: 700;
        }

        .pdf-viewer__page-error {
          color: #B91C1C;
        }

        .pdf-viewer__spinner {
          width: 22px;
          height: 22px;
          border: 3px solid #E2E8F0;
          border-top-color: var(--pdf-brand);
          border-radius: 999px;
          animation: pdfViewerSpin .8s linear infinite;
        }

        @keyframes pdfViewerSpin {
          to { transform: rotate(360deg); }
        }

        .pdf-viewer__text-layer,
        .pdf-viewer__annotation-layer {
          position: absolute;
          inset: 0;
          overflow: hidden;
        }

        .pdf-viewer__text-layer {
          z-index: 3;
          line-height: 1;
          opacity: 1;
          user-select: text;
          -webkit-user-select: text;
        }

        .pdf-viewer__text-item {
          position: absolute;
          display: block;
          overflow: hidden;
          color: transparent;
          white-space: pre;
          cursor: text;
          user-select: text;
          -webkit-user-select: text;
          transform-origin: 0 0;
        }

        .pdf-viewer__text-item::selection {
          background: rgba(37,99,235,.40);
        }

        .pdf-viewer__text-item.is-search-match {
          background: rgba(250,204,21,.46);
          border-radius: 2px;
        }

        .pdf-viewer__text-item.is-active-search-match {
          background: rgba(249,115,22,.58);
        }

        .pdf-viewer__annotation-layer {
          z-index: 4;
          pointer-events: none;
        }

        .pdf-viewer__annotation-link {
          position: absolute;
          display: block;
          padding: 0;
          border: 0;
          background: transparent;
          cursor: pointer;
          pointer-events: auto;
        }

        .pdf-viewer__annotation-link:hover,
        .pdf-viewer__annotation-link:focus-visible {
          outline: 2px solid rgba(37,99,235,.55);
          background: rgba(37,99,235,.08);
        }

        .pdf-viewer__page-number-label {
          margin-top: 7px;
          color: #CBD5E1;
          font-size: 11px;
          font-weight: 750;
        }

        .pdf-viewer__loading-screen,
        .pdf-viewer__error-screen {
          min-width: 0;
          min-height: 0;
          flex: 1;
          display: grid;
          place-items: center;
          padding: 24px;
          background: var(--pdf-viewer-bg);
          color: #FFFFFF;
          box-sizing: border-box;
        }

        .pdf-viewer__status-card {
          width: min(420px, 100%);
          display: grid;
          justify-items: center;
          gap: 13px;
          padding: 24px;
          border: 1px solid rgba(255,255,255,.12);
          border-radius: 16px;
          background: rgba(255,255,255,.06);
          box-shadow: 0 16px 38px rgba(0,0,0,.20);
          text-align: center;
          box-sizing: border-box;
        }

        .pdf-viewer__status-card h3 {
          margin: 0;
          font-size: 17px;
        }

        .pdf-viewer__status-card p {
          margin: 0;
          color: #CBD5E1;
          font-size: 12px;
          line-height: 1.55;
        }

        .pdf-viewer__progress-track {
          width: 100%;
          height: 7px;
          overflow: hidden;
          border-radius: 999px;
          background: rgba(255,255,255,.12);
        }

        .pdf-viewer__progress-value {
          height: 100%;
          border-radius: inherit;
          background: #60A5FA;
          transition: width .18s ease;
        }

        .pdf-viewer__status-actions {
          display: flex;
          justify-content: center;
          gap: 9px;
          flex-wrap: wrap;
        }

        .pdf-viewer__primary-action,
        .pdf-viewer__secondary-action {
          appearance: none;
          min-height: 38px;
          padding: 0 14px;
          border-radius: 9px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 850;
        }

        .pdf-viewer__primary-action {
          border: 1px solid var(--pdf-brand);
          background: var(--pdf-brand);
          color: #FFFFFF;
        }

        .pdf-viewer__secondary-action {
          border: 1px solid #CBD5E1;
          background: #FFFFFF;
          color: #334155;
        }

        .pdf-viewer__more-wrap {
          position: relative;
        }

        .pdf-viewer__more-menu {
          position: fixed;
          right: auto;
          max-width: calc(100vw - 16px);
          max-height: calc(100vh - 16px);
          overflow-y: auto;
          overscroll-behavior: contain;
          padding: 7px;
          border: 1px solid #CBD5E1;
          border-radius: 10px;
          background: #FFFFFF;
          color: #0F172A;
          box-shadow: 0 16px 42px rgba(15,23,42,.24);
          z-index: 1000;
        }

        .pdf-viewer__menu-button {
          appearance: none;
          width: 100%;
          min-height: 36px;
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 7px 9px;
          border: 0;
          border-radius: 7px;
          background: transparent;
          color: #0F172A;
          cursor: pointer;
          text-align: left;
          font-size: 12px;
          font-weight: 700;
        }

        .pdf-viewer__menu-button:hover:not(:disabled) {
          background: #F1F5F9;
        }

        .pdf-viewer__menu-button:disabled {
          cursor: not-allowed;
          opacity: .52;
        }

        .pdf-viewer__menu-divider {
          height: 1px;
          margin: 5px 0;
          background: #E2E8F0;
        }

        .pdf-viewer__dialog-backdrop {
          position: absolute;
          inset: 0;
          z-index: 100;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 14px;
          background: rgba(15,23,42,.58);
          backdrop-filter: blur(3px);
          box-sizing: border-box;
        }

        .pdf-viewer__dialog {
          max-height: calc(100% - 16px);
          overflow: auto;
          border: 1px solid #CBD5E1;
          border-radius: 14px;
          background: #FFFFFF;
          color: #0F172A;
          box-shadow: 0 24px 70px rgba(15,23,42,.34);
        }

        .pdf-viewer__dialog-header {
          position: sticky;
          top: 0;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 13px 15px;
          border-bottom: 1px solid #E2E8F0;
          background: #FFFFFF;
        }

        .pdf-viewer__dialog-close {
          appearance: none;
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #CBD5E1;
          border-radius: 8px;
          background: #FFFFFF;
          color: #475569;
          cursor: pointer;
        }

        .pdf-viewer__dialog-body {
          padding: 15px;
        }

        .pdf-viewer__properties {
          display: grid;
          grid-template-columns: minmax(120px, .65fr) minmax(0, 1.35fr);
          gap: 0;
          overflow: hidden;
          border: 1px solid #E2E8F0;
          border-radius: 10px;
        }

        .pdf-viewer__property-name,
        .pdf-viewer__property-value {
          padding: 9px 10px;
          border-bottom: 1px solid #E2E8F0;
          font-size: 11px;
          line-height: 1.4;
          overflow-wrap: anywhere;
        }

        .pdf-viewer__property-name {
          background: #F8FAFC;
          color: #475569;
          font-weight: 800;
        }

        .pdf-viewer__property-value {
          color: #0F172A;
          font-weight: 650;
        }

        .pdf-viewer__properties > :nth-last-child(-n + 2) {
          border-bottom: 0;
        }

        .pdf-viewer__password-dialog {
          width: min(390px, calc(100vw - 28px));
          display: grid;
          justify-items: stretch;
          gap: 12px;
          padding: 22px;
          box-sizing: border-box;
        }

        .pdf-viewer__password-icon {
          width: 46px;
          height: 46px;
          display: grid;
          place-items: center;
          justify-self: center;
          border-radius: 14px;
          background: #EEF2FF;
          color: var(--pdf-brand);
        }

        .pdf-viewer__password-dialog h3,
        .pdf-viewer__password-dialog p {
          margin: 0;
          text-align: center;
        }

        .pdf-viewer__password-dialog p {
          color: #64748B;
          font-size: 12px;
          line-height: 1.5;
        }

        .pdf-viewer__password-dialog input {
          width: 100%;
          height: 40px;
          padding: 0 11px;
          border: 1px solid #CBD5E1;
          border-radius: 9px;
          box-sizing: border-box;
          outline: none;
        }

        .pdf-viewer__password-dialog input:focus {
          border-color: #818CF8;
          box-shadow: 0 0 0 3px rgba(129,140,248,.15);
        }

        .pdf-viewer__password-actions {
          display: grid;
          grid-template-columns: 1fr 1.25fr;
          gap: 9px;
        }

        @media (max-width: 1023px) {
          .pdf-viewer__toolbar-track {
            grid-template-columns:
              minmax(120px, 180px)
              minmax(0, 1fr)
              auto;
            gap: 6px;
            padding: 6px 7px;
          }

          .pdf-viewer__title {
            max-width: 140px;
          }

          .pdf-viewer__toolbar-label {
            display: none;
          }

          .pdf-viewer__toolbar-button {
            width: 36px;
            padding: 0;
          }

          .pdf-viewer__toolbar-center-track {
            justify-content: flex-start;
          }
        }

        @media (max-width: 760px) {
          .pdf-viewer {
            min-height: 420px;
          }

          .pdf-viewer__toolbar,
          .pdf-viewer__toolbar-track {
            min-height: 50px;
          }

          .pdf-viewer__title {
            display: block;
            max-width: 110px;
          }

          .pdf-viewer__toolbar-divider {
            margin: 0 2px;
          }

          .pdf-viewer__zoom-select {
            min-width: 82px;
          }

          .pdf-viewer__sidebar {
            position: absolute;
            inset: 0 auto 0 0;
            width: min(82vw, 280px);
            min-width: min(82vw, 280px);
            box-shadow: 18px 0 40px rgba(0,0,0,.38);
          }

          .pdf-viewer__sidebar::after {
            content: "";
            position: fixed;
            inset: 0;
            left: min(82vw, 280px);
            background: rgba(0,0,0,.32);
            pointer-events: none;
          }

          .pdf-viewer__document {
            gap: 12px;
            padding: 12px 12px 34px;
          }

          .pdf-viewer__search-bar {
            align-items: stretch;
            flex-wrap: wrap;
          }

          .pdf-viewer__search-input-wrap {
            flex-basis: calc(100% - 42px);
          }

          .pdf-viewer__search-count {
            order: 5;
            flex: 1;
            min-width: 90px;
            text-align: left;
          }

          .pdf-viewer__search-option {
            order: 6;
          }
        }

        @media (max-width: 520px) {
          .pdf-viewer__toolbar-button {
            min-width: 34px;
            width: 34px;
            height: 34px;
          }

          .pdf-viewer__page-control {
            gap: 4px;
          }

          .pdf-viewer__page-input {
            width: 40px;
          }

          .pdf-viewer__zoom-select {
            min-width: 74px;
            width: 74px;
          }

          .pdf-viewer__search-option {
            font-size: 10px;
          }

          .pdf-viewer__dialog-body {
            padding: 12px;
          }

          .pdf-viewer__properties {
            grid-template-columns: 1fr;
          }

          .pdf-viewer__property-name {
            border-bottom: 0;
            padding-bottom: 3px;
          }

          .pdf-viewer__property-value {
            padding-top: 3px;
          }

          .pdf-viewer__properties > :nth-last-child(-n + 2) {
            border-bottom: 1px solid #E2E8F0;
          }

          .pdf-viewer__properties > :last-child {
            border-bottom: 0;
          }
        }
      `}</style>

      <div
        className="pdf-viewer__toolbar"
        aria-label="PDF controls"
      >
        <div className="pdf-viewer__toolbar-track">
          <div className="pdf-viewer__toolbar-section is-left">
            <ToolbarButton
              icon="menu"
              label="Sidebar"
              compact
              active={sidebarOpen}
              onClick={() => setSidebarOpen((value) => !value)}
            />

            {showTitle ? (
              <div
                className="pdf-viewer__title"
                title={resolvedTitle}
              >
                {resolvedTitle || "PDF Document"}
              </div>
            ) : null}
          </div>

          <div className="pdf-viewer__toolbar-center-shell">
            {toolbarOverflow.left ? (
              <button
                type="button"
                className="pdf-viewer__toolbar-scroll-button is-left"
                aria-label="Show earlier PDF controls"
                title="Show earlier PDF controls"
                onClick={() => scrollToolbarControls(-1)}
              >
                <Icon name="chevronLeft" size={18} />
              </button>
            ) : null}

            <div
              ref={toolbarRef}
              className="pdf-viewer__toolbar-center-scroll"
              data-dragging="false"
              onScroll={updateToolbarOverflow}
              onPointerDown={handleToolbarPointerDown}
              onPointerMove={handleToolbarPointerMove}
              onPointerUp={finishToolbarPointerDrag}
              onPointerCancel={finishToolbarPointerDrag}
              onLostPointerCapture={finishToolbarPointerDrag}
              onClickCapture={handleToolbarClickCapture}
              onDragStart={(event) => event.preventDefault()}
              aria-label="Scrollable PDF tools"
              title="Drag left or right to reveal more PDF tools"
            >
              <div className="pdf-viewer__toolbar-center-track">
                <ToolbarButton
                  icon="firstPage"
                  label="First page"
                  compact
                  disabled={status !== "ready" || pageNumber <= 1}
                  onClick={() => scrollToPage(1)}
                />

                <ToolbarButton
                  icon="chevronLeft"
                  label="Previous page"
                  compact
                  disabled={status !== "ready" || pageNumber <= 1}
                  onClick={() => scrollToPage(pageNumber - 1)}
                />

                <div className="pdf-viewer__page-control">
                  <input
                    className="pdf-viewer__page-input"
                    value={pageDraft}
                    inputMode="numeric"
                    aria-label="Current page"
                    disabled={status !== "ready"}
                    onChange={(event) =>
                      setPageDraft(
                        event.target.value.replace(/[^\d]/g, "")
                      )
                    }
                    onBlur={commitPageDraft}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        commitPageDraft();
                        event.currentTarget.blur();
                      }
                    }}
                  />
                  <span aria-label={`${numPages} total pages`}>
                    / {numPages || "—"}
                  </span>
                </div>

                <ToolbarButton
                  icon="chevronRight"
                  label="Next page"
                  compact
                  disabled={
                    status !== "ready" || pageNumber >= numPages
                  }
                  onClick={() => scrollToPage(pageNumber + 1)}
                />

                <ToolbarButton
                  icon="lastPage"
                  label="Last page"
                  compact
                  disabled={
                    status !== "ready" || pageNumber >= numPages
                  }
                  onClick={() => scrollToPage(numPages)}
                />

                <span className="pdf-viewer__toolbar-divider" />

                <ToolbarButton
                  icon="minus"
                  label="Zoom out"
                  compact
                  disabled={
                    status !== "ready" ||
                    effectiveZoomPercent <= MIN_ZOOM
                  }
                  onClick={zoomOut}
                />

                <select
                  className="pdf-viewer__zoom-select"
                  aria-label="Zoom level"
                  value={zoomSelectValue}
                  disabled={status !== "ready"}
                  onChange={(event) =>
                    handleZoomSelect(event.target.value)
                  }
                >
                  {!ZOOM_OPTIONS.some(
                    (option) => option.value === zoomSelectValue
                  ) ? (
                    <option value={zoomSelectValue}>
                      {effectiveZoomPercent}%
                    </option>
                  ) : null}

                  {ZOOM_OPTIONS.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>

                <ToolbarButton
                  icon="plus"
                  label="Zoom in"
                  compact
                  disabled={
                    status !== "ready" ||
                    effectiveZoomPercent >= MAX_ZOOM
                  }
                  onClick={zoomIn}
                />

                <ToolbarButton
                  icon="fitWidth"
                  label="Fit width"
                  compact
                  active={zoomMode === "fit-width"}
                  disabled={status !== "ready"}
                  onClick={() => setZoomMode("fit-width")}
                />

                <ToolbarButton
                  icon="fitPage"
                  label="Fit page"
                  compact
                  active={zoomMode === "fit-page"}
                  disabled={status !== "ready"}
                  onClick={() => setZoomMode("fit-page")}
                />

                <ToolbarButton
                  icon="rotate"
                  label="Rotate clockwise"
                  compact
                  disabled={status !== "ready"}
                  onClick={rotateClockwise}
                />

                <ToolbarButton
                  icon="search"
                  label="Search"
                  compact
                  active={searchOpen}
                  disabled={status !== "ready"}
                  onClick={() =>
                    setSearchOpen((value) => !value)
                  }
                />

                {allowPrint ? (
                  <ToolbarButton
                    icon="print"
                    label="Print"
                    compact
                    disabled={
                      status !== "ready" || Boolean(actionBusy)
                    }
                    onClick={handlePrint}
                  />
                ) : null}

                {allowDownload ? (
                  <ToolbarButton
                    icon="download"
                    label="Download"
                    compact
                    disabled={
                      status !== "ready" || Boolean(actionBusy)
                    }
                    onClick={handleDownload}
                  />
                ) : null}

                {allowFullscreen ? (
                  <ToolbarButton
                    icon={
                      isFullscreen
                        ? "fullscreenExit"
                        : "fullscreen"
                    }
                    label={
                      isFullscreen
                        ? "Exit fullscreen"
                        : "Fullscreen"
                    }
                    compact
                    disabled={status !== "ready"}
                    onClick={toggleFullscreen}
                  />
                ) : null}
              </div>
            </div>

            {toolbarOverflow.right ? (
              <button
                type="button"
                className="pdf-viewer__toolbar-scroll-button is-right"
                aria-label="Show more PDF controls"
                title="Show more PDF controls"
                onClick={() => scrollToolbarControls(1)}
              >
                <Icon name="chevronRight" size={18} />
              </button>
            ) : null}
          </div>

          <div className="pdf-viewer__toolbar-section is-right">
            <div
              ref={moreMenuRef}
              className="pdf-viewer__more-wrap"
            >
              <ToolbarButton
                icon="more"
                label="More options"
                compact
                active={moreOpen}
                onClick={() =>
                  setMoreOpen((value) => !value)
                }
              />

              {moreOpen ? (
                <div
                  ref={morePopupRef}
                  className="pdf-viewer__more-menu"
                  role="menu"
                  style={{
                    top: moreMenuPosition.top,
                    left: moreMenuPosition.left,
                    width: moreMenuPosition.width,
                    visibility: moreMenuPosition.ready ? "visible" : "hidden",
                  }}
                >
                  <button
                    type="button"
                    className="pdf-viewer__menu-button"
                    onClick={() => {
                      setCustomZoom(100);
                      setMoreOpen(false);
                    }}
                  >
                    <Icon name="fitPage" size={17} />
                    Actual size
                  </button>

                  <button
                    type="button"
                    className="pdf-viewer__menu-button"
                    onClick={() => {
                      setZoomMode("fit-width");
                      setMoreOpen(false);
                    }}
                  >
                    <Icon name="fitWidth" size={17} />
                    Fit width
                  </button>

                  <button
                    type="button"
                    className="pdf-viewer__menu-button"
                    onClick={() => {
                      setZoomMode("fit-page");
                      setMoreOpen(false);
                    }}
                  >
                    <Icon name="fitPage" size={17} />
                    Fit page
                  </button>

                  <div className="pdf-viewer__menu-divider" />

                  <button
                    type="button"
                    className="pdf-viewer__menu-button"
                    onClick={() => {
                      setPropertiesOpen(true);
                      setMoreOpen(false);
                    }}
                  >
                    <Icon name="info" size={17} />
                    Document properties
                  </button>

                  {canUseBrowserFallback ? (
                    <button
                      type="button"
                      className="pdf-viewer__menu-button"
                      disabled={Boolean(actionBusy)}
                      onClick={() => {
                        setMoreOpen(false);
                        openBrowserFallback();
                      }}
                    >
                      <Icon name="browser" size={17} />
                      {actionBusy === "browser"
                        ? "Opening browser…"
                        : "Open with browser"}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {searchOpen ? (
        <div className="pdf-viewer__search-bar">
          <div className="pdf-viewer__search-input-wrap">
            <Icon name="search" size={17} />
            <input
              ref={searchInputRef}
              className="pdf-viewer__search-input"
              value={searchTerm}
              placeholder="Find in document"
              aria-label="Find in document"
              onChange={(event) =>
                setSearchTerm(event.target.value)
              }
            />
          </div>

          <span
            className="pdf-viewer__search-count"
            aria-live="polite"
          >
            {visibleSearchLabel}
          </span>

          <button
            type="button"
            className="pdf-viewer__search-action"
            onClick={() => moveSearch(-1)}
            disabled={!searchResults.length || searchBusy}
            aria-label="Previous match"
            title="Previous match"
          >
            <Icon name="chevronUp" size={17} />
          </button>

          <button
            type="button"
            className="pdf-viewer__search-action"
            onClick={() => moveSearch(1)}
            disabled={!searchResults.length || searchBusy}
            aria-label="Next match"
            title="Next match"
          >
            <Icon name="chevronDown" size={17} />
          </button>

          <label className="pdf-viewer__search-option">
            <input
              type="checkbox"
              checked={searchCaseSensitive}
              onChange={(event) =>
                setSearchCaseSensitive(event.target.checked)
              }
            />
            Match case
          </label>

          <label className="pdf-viewer__search-option">
            <input
              type="checkbox"
              checked={searchWholeWord}
              onChange={(event) =>
                setSearchWholeWord(event.target.checked)
              }
            />
            Whole words
          </label>

          <button
            type="button"
            className="pdf-viewer__search-action"
            onClick={() => setSearchOpen(false)}
            aria-label="Close search"
            title="Close search"
          >
            <Icon name="close" size={17} />
          </button>
        </div>
      ) : null}

      <div className="pdf-viewer__body">
        {sidebarOpen && status === "ready" ? (
          <aside
            className="pdf-viewer__sidebar"
            aria-label="PDF navigation"
          >
            <div className="pdf-viewer__sidebar-tabs">
              <button
                type="button"
                className={`pdf-viewer__sidebar-tab ${
                  sidebarTab === "thumbnails"
                    ? "is-active"
                    : ""
                }`}
                onClick={() => setSidebarTab("thumbnails")}
                aria-label="Page thumbnails"
                title="Page thumbnails"
              >
                <Icon name="thumbnails" size={18} />
              </button>

              <button
                type="button"
                className={`pdf-viewer__sidebar-tab ${
                  sidebarTab === "outline"
                    ? "is-active"
                    : ""
                }`}
                onClick={() => setSidebarTab("outline")}
                aria-label="Document outline"
                title="Document outline"
              >
                <Icon name="outline" size={18} />
              </button>

              <button
                type="button"
                className={`pdf-viewer__sidebar-tab ${
                  sidebarTab === "attachments"
                    ? "is-active"
                    : ""
                }`}
                onClick={() => setSidebarTab("attachments")}
                aria-label="Attachments"
                title="Attachments"
              >
                <Icon name="attachment" size={18} />
              </button>
            </div>

            <div className="pdf-viewer__sidebar-content">
              {sidebarTab === "thumbnails" ? (
                <div className="pdf-viewer__thumbnail-list">
                  {Array.from(
                    { length: numPages },
                    (_, index) => index + 1
                  ).map((number) => (
                    <PdfThumbnail
                      key={number}
                      pdfDocument={pdfDocument}
                      pageNumber={number}
                      active={number === pageNumber}
                      rotation={rotation}
                      onClick={(requestedPage) => {
                        scrollToPage(requestedPage);
                        if (window.innerWidth <= 760) {
                          setSidebarOpen(false);
                        }
                      }}
                    />
                  ))}
                </div>
              ) : null}

              {sidebarTab === "outline" ? (
                outline.length ? (
                  <OutlineTree
                    items={outline}
                    onNavigate={(destination) => {
                      navigateDestination(destination);
                      if (window.innerWidth <= 760) {
                        setSidebarOpen(false);
                      }
                    }}
                  />
                ) : (
                  <div className="pdf-viewer__sidebar-empty">
                    This PDF does not contain a document outline.
                  </div>
                )
              ) : null}

              {sidebarTab === "attachments" ? (
                attachments.length ? (
                  <div className="pdf-viewer__attachments">
                    {attachments.map((attachment, index) => (
                      <div
                        key={`${attachment.name}-${index}`}
                        className="pdf-viewer__attachment"
                        title={
                          attachment.description ||
                          attachment.filename
                        }
                      >
                        <span className="pdf-viewer__attachment-name">
                          {attachment.filename ||
                            attachment.name}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            downloadAttachment(attachment)
                          }
                          aria-label={`Download ${
                            attachment.filename ||
                            attachment.name
                          }`}
                          title="Download attachment"
                        >
                          <Icon name="download" size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="pdf-viewer__sidebar-empty">
                    This PDF does not contain attachments.
                  </div>
                )
              ) : null}
            </div>
          </aside>
        ) : null}

        {status === "loading" ? (
          <div className="pdf-viewer__loading-screen">
            <div className="pdf-viewer__status-card">
              <span className="pdf-viewer__spinner" />
              <h3>Opening PDF</h3>
              <p>
                Loading and preparing the document for secure
                in-app viewing.
              </p>

              <div className="pdf-viewer__progress-track">
                <div
                  className="pdf-viewer__progress-value"
                  style={{
                    width: `${
                      progress.total > 0
                        ? progress.percent
                        : 18
                    }%`,
                  }}
                />
              </div>

              <p>
                {progress.total > 0
                  ? `${progress.percent}% · ${formatBytes(
                      progress.loaded
                    )} of ${formatBytes(progress.total)}`
                  : progress.loaded > 0
                  ? formatBytes(progress.loaded)
                  : "Preparing document…"}
              </p>
            </div>
          </div>
        ) : null}

        {status === "error" ? (
          <div className="pdf-viewer__error-screen">
            <div className="pdf-viewer__status-card">
              <Icon name="info" size={30} />
              <h3>PDF preview unavailable</h3>
              <p>
                {error ||
                  "The PDF could not be displayed in the app."}
              </p>

              <div className="pdf-viewer__status-actions">
                <button
                  type="button"
                  className="pdf-viewer__primary-action"
                  onClick={loadDocument}
                >
                  <Icon name="retry" size={16} /> Retry
                </button>

                {canUseBrowserFallback ? (
                  <button
                    type="button"
                    className="pdf-viewer__secondary-action"
                    onClick={openBrowserFallback}
                    disabled={Boolean(actionBusy)}
                  >
                    {actionBusy === "browser"
                      ? "Opening browser…"
                      : "Open with browser"}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {status === "ready" ? (
          <main
            ref={documentAreaRef}
            className="pdf-viewer__document-scroll"
            onScroll={handleDocumentScroll}
            aria-label="PDF pages"
          >
            <div className="pdf-viewer__document">
              {Array.from(
                { length: numPages },
                (_, index) => index + 1
              ).map((number) => (
                <PdfPage
                  key={`${number}-${rotation}`}
                  pdfDocument={pdfDocument}
                  pageNumber={number}
                  zoomMode={zoomMode}
                  zoomPercent={zoomPercent}
                  rotation={rotation}
                  availableWidth={containerSize.width}
                  availableHeight={containerSize.height}
                  searchTerm={searchTerm.trim()}
                  searchCaseSensitive={searchCaseSensitive}
                  searchWholeWord={searchWholeWord}
                  activeSearchPage={
                    activeSearchResult?.pageNumber === number
                  }
                  onRegister={registerPage}
                  onNavigateDestination={navigateDestination}
                />
              ))}
            </div>
          </main>
        ) : null}
      </div>

      <Modal
        open={propertiesOpen}
        title="Document properties"
        onClose={() => setPropertiesOpen(false)}
        width={560}
      >
        {metadataRows.length ? (
          <div className="pdf-viewer__properties">
            {metadataRows.flatMap(([name, value]) => [
              <div
                key={`${name}-name`}
                className="pdf-viewer__property-name"
              >
                {name}
              </div>,
              <div
                key={`${name}-value`}
                className="pdf-viewer__property-value"
              >
                {value}
              </div>,
            ])}
          </div>
        ) : (
          <p
            style={{
              margin: 0,
              color: "#64748B",
              fontSize: 12,
            }}
          >
            No document metadata is available.
          </p>
        )}
      </Modal>

      <PasswordDialog
        open={passwordState.open}
        incorrect={passwordState.incorrect}
        busy={passwordState.busy}
        onSubmit={submitPassword}
        onCancel={cancelPassword}
      />
    </div>
  );
}
